import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Chip, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Tooltip, Collapse,
  Divider, CircularProgress,
  Popover, useTheme, Alert,
  Select, MenuItem, FormControl, Checkbox,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  AccountBalance as BankIcon,
  Sync as SyncIcon,
  LinkOff as DisconnectIcon,
  Upload as UploadIcon,
  Receipt as ReceiptIcon,
  AddAPhoto as PhotoIcon,
  Add as AddIcon,
  Calculate as CalculateIcon,
  CalendarMonth as CalendarIcon,
  Backspace as BackspaceIcon,
  Description as StatementIcon,
  WarningAmber as WarningIcon,
  InfoOutlined as InfoIcon,
  AddCircleOutline as AddBankIcon,
  History as ResyncIcon,
  Visibility as ViewIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { ChartsDataProvider } from '@mui/x-charts/ChartsDataProvider';
import { ChartsSurface } from '@mui/x-charts/ChartsSurface';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { useXScale, useYScale, useDrawingArea } from '@mui/x-charts/hooks';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';
import InvoiceCard from '../components/InvoiceCard';
import InvoiceUpload from '../components/InvoiceUpload';
import ReceiptCard from '../components/ReceiptCard';
import ReceiptUpload from '../components/ReceiptUpload';
import BankStatementUpload from '../components/BankStatementUpload';
import BankStatementDetailDialog from '../components/BankStatementDetailDialog';
import BankConnectionDetailDialog from '../components/BankConnectionDetailDialog';
import UploadProofDialog from '../components/UploadProofDialog';
import ProofViewDialog from '../components/ProofViewDialog';
import ConfirmNewExpenseDialog, { PendingInvoice } from '../components/ConfirmNewExpenseDialog';
import BulkInvoiceReviewDialog from '../components/BulkInvoiceReviewDialog';
import AddCashTransactionDialog, { EligibleDocument, UploadNewCashOutcome } from '../components/AddCashTransactionDialog';

// ── types ─────────────────────────────────────────────────────────────────────

interface Summary {
  collected: string;
  outstanding: string;
  deposits_held: string;
  total_spent: string;
  total_spent_invoices: string;
  total_spent_receipts: string;
  total_spent_statements: string;
  // Income/Expenses are sourced EXCLUSIVELY from whichever bank_source the shared account-scope
  // selector picked (live feed vs uploaded statements, optionally filtered to specific
  // connections) — never a blend of both, and never invoices/receipts. See routes/accounting.py's
  // get_summary()/_bank_income_expenses_for_range() — the exact same helper the chart itself
  // calls, so these two figures and the chart's totals can never disagree for the same selection.
  expenses_general: string;
  bank_income: string;
  // Sum of tax/VAT extracted from invoices/receipts for the period (see
  // routes/accounting.py's _sum_vat_for_range) — vat_out is VAT on money spent (reclaimable
  // input VAT), vat_in is VAT on a manually-recorded money-in cash entry (output VAT owed).
  vat_out: string;
  vat_in: string;
}

interface MonthlyTrendPoint {
  label: string;
  months?: string; // only present when granularity='quarterly'/'weekly' — e.g. "Apr, May, Jun 2026" for tooltip
  month_label?: string; // weekly only — which month's slot this week belongs to, e.g. "Jun 2026"
  income: string;
  expenses: string;
  net: string;
  // Sum of tax/VAT extracted from invoices/receipts falling in this bucket (see
  // routes/accounting.py's _sum_vat_for_range) — vat_out is VAT on money spent, vat_in is VAT
  // on a manually-recorded money-in cash entry. Purely informational, not folded into net.
  vat_out?: string;
  vat_in?: string;
}

// Module-scope (not component state) so it survives a full unmount/remount of AccountingPage —
// e.g. navigating to another page and back. Without this, the chart's own state resets to empty
// and monthlyTrendLoading resets to true on every remount, showing a spinner-then-chart flash
// each time even though the underlying data (and the period/granularity filters that produced
// it, themselves persisted via readAccountingLS below) almost certainly hasn't changed. Holding
// the last-rendered trend data here lets the chart paint immediately on remount while a silent
// background refetch (see hasLoadedTrendOnce) confirms/updates it in place.
let cachedTrendData: {
  monthly: MonthlyTrendPoint[]; quarterly: MonthlyTrendPoint[]; weekly: MonthlyTrendPoint[]; daily: MonthlyTrendPoint[];
} | null = null;

interface BankConnection {
  id: string;
  provider: 'truelayer';
  owner_id: string | null;
  institution_name: string | null;
  account_name: string | null;
  account_mask: string | null;
  account_holder_name: string | null;
  account_number: string | null;
  account_iban: string | null;
  status: string;
  last_synced_at: string | null;
  created_at: string | null;
}

// A "book" entity (person or business) — see Owner in backend/models.py. Only the fields the
// entity selector/connection-assign dropdown need; the full CRUD shape lives in PropertiesPage.tsx.
interface Owner {
  id: string;
  name: string;
  is_owner: boolean;
}

interface BankTransactionRow {
  id: string;
  bank_connection_id?: string;
  amount: string;
  iso_currency_code: string | null;
  date: string;
  name: string | null;
  merchant_name: string | null;
  category: string | null;
  pending: boolean;
  excluded?: boolean;
  tag?: string | null;
  linked_proofs?: InvoiceRecord[];
  isCash?: boolean;
  sourceInvoice?: InvoiceRecord;
}

interface BankStatementTransaction {
  id: string;
  date: string;
  value_date: string | null;
  description: string | null;
  counterparty: string | null;
  debit_amount: string;
  credit_amount: string;
  balance_after: string;
  direction: 'money_in' | 'money_out';
  payment_method: string | null;
  category_hint: string | null;
  tag?: string | null;
  excluded?: boolean;
  linked_proofs?: InvoiceRecord[];
}

interface BankStatementRecord {
  id: string;
  bank_name: string | null;
  account_holder_name: string | null;
  account_number: string | null;
  iban: string | null;
  currency: string;
  statement_date: string | null;
  period_start_date: string | null;
  period_end_date: string | null;
  opening_balance: string | null;
  closing_balance: string | null;
  total_debits: string | null;
  total_credits: string | null;
  debits_mismatch: boolean;
  credits_mismatch: boolean;
  balance_mismatch: boolean;
  original_filename: string | null;
  created_at: string;
  transaction_count: number;
  transactions?: BankStatementTransaction[];
}

interface InvoiceLineItem { id: string; description: string; cost: string; order: number; included: boolean; }

interface InvoiceRecord {
  id: string; invoice_number: string; invoice_date: string; contractor_name: string;
  property_address: string; normalized_address: string; flat_label?: string;
  subtotal: string; tax: string; total: string; document_type?: string;
  effective_total: string;
  pdf_filename: string; original_filename: string;
  items: InvoiceLineItem[]; created_at: string;
  bank_transaction_id?: string | null;
  bank_statement_transaction_id?: string | null;
  confirmed?: boolean;
  excluded_from_transaction_list?: boolean;
  direction?: 'money_out' | 'money_in';
}

// First-page thumbnail for an uploaded bank statement — same convention (and same click-to-open-
// the-real-PDF-in-a-new-tab behavior) as certificates/ReceiptCard/InvoiceCard's own preview
// images, just extracted as its own component since it needs per-row error state inside a
// .map() (an <img>'s onError can't conditionally render a fallback icon without one).
const StatementThumbnail: React.FC<{ statementId: string; createdAt: string }> = ({ statementId, createdAt }) => {
  const { palette } = useTheme();
  const [errored, setErrored] = useState(false);
  const openPdf = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(`${config.apiUrl}/api/bank-statements/${statementId}/view`, '_blank');
  };
  return (
    <Box
      onClick={openPdf}
      sx={{
        width: 44, alignSelf: 'stretch', borderRadius: 1, overflow: 'hidden', flexShrink: 0,
        border: `1px solid ${palette.tokens.hairline}`, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        '&:hover': { opacity: 0.8, borderColor: palette.tokens.primary },
      }}
    >
      {errored ? (
        <StatementIcon sx={{ fontSize: 18, color: palette.tokens.muted }} />
      ) : (
        <img
          src={`${config.apiUrl}/api/bank-statements/${statementId}/preview?t=${createdAt}`}
          alt="Statement preview"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={() => setErrored(true)}
        />
      )}
    </Box>
  );
};

// Shared Income/Expenses/Net colors — used identically by the KPI cards and the chart (see
// TrendBars/legend below) so the same category always reads as the same color everywhere on
// this page. Income = blue, Expenses = red, Net = plain black — per direct request.
const TREND_COLORS = { income: '#2a78d6', expenses: '#d32f2f', net: '#000000' } as const;

const fmtGBP = (val: string | number | undefined | null) => {
  const n = Number(val);
  return `£${(Number.isFinite(n) ? n : 0).toLocaleString('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;
};

// TrueLayer returns institution_name in ALL CAPS ("STARLING", "REVOLUT") — title-cased for display.
const prettyInstitutionName = (name: string | null | undefined) => {
  if (!name) return 'Connected bank';
  return name.replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
};

// Compact "4 Aug, 14:19" instead of the full "04/08/2026, 14:19:12" — a sync timestamp doesn't
// need seconds or a 4-digit year to be useful at a glance.
const fmtSyncTime = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

// "01 Jun 2026" instead of a raw "2026-06-01" — same date format certificates already use
// (EnhancedCertificateCard.tsx's own formatDate), so a statement's period reads the same way.
const fmtCertStyleDate = (dateStr: string) => {
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

// "Which statement is this" at a glance — the month it covers, not the bank/account details
// (those belong in a subtitle next to this). A period spanning more than one calendar month
// (rare, but possible) falls back to a short range instead of a misleading single-month label.
// Shared by the "Your banks" panel's statement rows and the full "Uploaded statements" dialog,
// so the two can't drift apart.
const statementPeriodLabel = (stmt: { period_start_date: string | null; period_end_date: string | null; statement_date: string | null }) => {
  if (stmt.period_start_date) {
    const start = new Date(stmt.period_start_date);
    const end = stmt.period_end_date ? new Date(stmt.period_end_date) : start;
    const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
    return sameMonth
      ? start.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
      : `${start.toLocaleDateString('en-GB', { month: 'short' })} – ${end.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`;
  }
  if (stmt.statement_date) return fmtCertStyleDate(stmt.statement_date);
  return 'Bank statement';
};

// Bank statement amounts need their own formatter, deliberately NOT fmtGBP above: fmtGBP rounds
// to whole pounds (fine for rent/property KPI cards) and hardcodes "£", which silently mangled a
// real EUR statement's amounts (911.68 displayed as "£912" — wrong currency symbol AND wrong
// number). Statement transactions need exact cents for bookkeeping and the statement's own
// currency, not GBP.
const fmtStatementAmount = (val: string | number, currency: string | null | undefined) => {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency', currency: currency || 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Number(val));
  } catch {
    return `${Number(val).toFixed(2)} ${currency || ''}`.trim();
  }
};

// Compact y-axis tick labels (£5k instead of £5,000) — full currency formatting was wide enough
// that ticks like £10,000/-£10,000 got clipped against the chart's left margin once the seeded
// demo data pushed the range past ±£5,000.
const fmtGBPCompact = (val: number) => {
  const sign = val < 0 ? '-' : '';
  const abs = Math.abs(val);
  if (abs >= 1000) {
    const thousands = abs / 1000;
    return `${sign}£${thousands % 1 === 0 ? thousands : thousands.toFixed(1)}k`;
  }
  return `${sign}£${abs}`;
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
// "Jul 2026" -> "2026-07" — matches BankConnectionDetailDialog's own txn.date.slice(0, 7) month-
// group keys, so the chart's already-resolved period (monthlyTrend) can tell "View transactions"
// which month(s) to expand by default without a second date-range computation.
const monthLabelToKey = (label: string): string | null => {
  const [monAbbr, yearStr] = label.split(' ');
  const idx = MONTHS.indexOf(monAbbr);
  if (idx === -1 || !yearStr) return null;
  return `${yearStr}-${String(idx + 1).padStart(2, '0')}`;
};
const currentMonthLabel = () => {
  const d = new Date();
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
const lastMonthLabel = () => {
  const d = new Date();
  d.setDate(1); // pin to the 1st first so subtracting a month can't roll over to a short month's last day
  d.setMonth(d.getMonth() - 1);
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
type PeriodType = 'month' | 'last_month' | 'this_year' | 'last_year' | 'this_quarter' | 'last_quarter' | 'custom' | 'all';
type YearType = 'calendar' | 'fiscal';
type ChartGranularity = 'monthly' | 'quarterly' | 'weekly' | 'daily';

// Which granularities the Income vs Expenses chart offers for each period, and which one it
// defaults to when the period changes — the chart's time span now follows the shared period
// dropdown (This month/This quarter/This year/...) instead of always showing the current year.
// 'all' isn't listed since the backend silently treats it as 'this_year' (see
// _resolve_period_range) — no bounded start date to bucket an unbounded range from.
const GRANULARITY_OPTIONS_BY_PERIOD: Record<PeriodType, { options: Array<{ key: ChartGranularity; label: string }>; default: ChartGranularity }> = {
  this_year: { options: [{ key: 'monthly', label: 'Monthly' }, { key: 'quarterly', label: 'Quarterly' }, { key: 'weekly', label: 'Weekly' }], default: 'monthly' },
  last_year: { options: [{ key: 'monthly', label: 'Monthly' }, { key: 'quarterly', label: 'Quarterly' }, { key: 'weekly', label: 'Weekly' }], default: 'monthly' },
  this_quarter: { options: [{ key: 'quarterly', label: 'Quarterly' }, { key: 'monthly', label: 'Monthly' }, { key: 'weekly', label: 'Weekly' }, { key: 'daily', label: 'Daily' }], default: 'monthly' },
  last_quarter: { options: [{ key: 'quarterly', label: 'Quarterly' }, { key: 'monthly', label: 'Monthly' }, { key: 'weekly', label: 'Weekly' }, { key: 'daily', label: 'Daily' }], default: 'monthly' },
  month: { options: [{ key: 'weekly', label: 'Weekly' }, { key: 'daily', label: 'Daily' }], default: 'daily' },
  last_month: { options: [{ key: 'weekly', label: 'Weekly' }, { key: 'daily', label: 'Daily' }], default: 'daily' },
  custom: { options: [{ key: 'quarterly', label: 'Quarterly' }, { key: 'monthly', label: 'Monthly' }, { key: 'weekly', label: 'Weekly' }, { key: 'daily', label: 'Daily' }], default: 'monthly' },
  all: { options: [{ key: 'monthly', label: 'Monthly' }, { key: 'quarterly', label: 'Quarterly' }, { key: 'weekly', label: 'Weekly' }], default: 'monthly' },
};

// Matches the backend's own ~100-day cap on 'daily' (get_monthly_trend silently downgrades to
// weekly-bucketed data beyond this span — see its docstring). Every other period's options above
// are hard-coded because their span is always safely under this cap (a quarter is ~92 days at
// most), but a Custom range has no such guarantee — a long Custom range offering "Daily" would
// let the user pick it, the backend would silently substitute weekly buckets, and the frontend
// would go on treating that response as if it were genuinely one-bar-per-day (wrong bar widths/
// positions within each month's slot, a net-line that zigzags between far fewer points than
// expected, and a hover tooltip whose date range belongs to a whole week rather than the single
// day the bar visually occupies) without any error or indication of the mismatch.
const MAX_DAILY_SPAN_DAYS = 100;

// Custom's own options need to exclude 'Daily' once the selected custom range would exceed the
// backend's cap (see MAX_DAILY_SPAN_DAYS above) — every other period is safely within the cap
// already, so this only ever changes anything for 'custom'.
function granularityOptionsFor(period: PeriodType, customStart?: string, customEnd?: string) {
  const base = GRANULARITY_OPTIONS_BY_PERIOD[period];
  if (period !== 'custom' || !customStart || !customEnd) return base;
  const spanDays = Math.abs(new Date(customEnd).getTime() - new Date(customStart).getTime()) / 86400000;
  if (spanDays <= MAX_DAILY_SPAN_DAYS) return base;
  return { ...base, options: base.options.filter(o => o.key !== 'daily') };
}

// This page's filter/view choices (period, account selection, chart granularity, etc.) persist
// to localStorage so navigating away and back — or a full reload — restores the page exactly
// as the user left it, rather than silently resetting to defaults every time it remounts (this
// page is behind a react-router route, so it fully unmounts on navigation). Same convention as
// the existing `accounting_mode`/`accounting_bank_expanded` keys elsewhere on this page: a fast,
// good-enough localStorage persistence, not a real per-account server-side preference.
const ACCOUNTING_LS_KEYS = {
  period: 'accounting_filter_period',
  yearType: 'accounting_filter_year_type',
  ownerId: 'accounting_filter_owner_id',
  bankSource: 'accounting_filter_bank_source',
  connectionIds: 'accounting_filter_connection_ids',
  includeManualEntries: 'accounting_filter_include_manual_entries',
  chartGranularity: 'accounting_filter_chart_granularity',
  customStart: 'accounting_filter_custom_start',
  customEnd: 'accounting_filter_custom_end',
} as const;

function readAccountingLS<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeAccountingLS(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage unavailable (private browsing, quota, etc.) — persistence is a nice-to-have,
    // never worth breaking the page over.
  }
}

// "15 Feb – 20 Mar 2026", or "15 – 20 Feb 2026" when both ends fall in the same month, or with
// only one year shown when both ends share a year — never repeats what's already implied.
const formatCustomRangeLabel = (startStr: string, endStr: string): string => {
  if (!startStr || !endStr) return 'Pick a range';
  const start = new Date(`${startStr}T00:00:00`);
  const end = new Date(`${endStr}T00:00:00`);
  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  const startLabel = sameMonth
    ? `${start.getDate()}`
    : `${start.getDate()} ${MONTHS[start.getMonth()]}${sameYear ? '' : ` ${start.getFullYear()}`}`;
  const endLabel = `${end.getDate()} ${MONTHS[end.getMonth()]} ${end.getFullYear()}`;
  return `${startLabel} – ${endLabel}`;
};

const fiscalYearLabel = (startYear: number) => `FY ${startYear}/${String((startYear + 1) % 100).padStart(2, '0')}`;

// Quarters always land on Jan/Apr/Jul/Oct boundaries regardless of yearType — calendar Q1
// (Jan-Mar) and fiscal Q4 (Jan-Mar) are the same 3 months. yearType only changes the label
// (which quarter number it's called, and which year it's attributed to) — mirrors the backend's
// _resolve_period_months/_quarter_label in routes/accounting.py.
const thisQuarterStart = (today: Date) => {
  const m = today.getMonth() + 1;
  return { year: today.getFullYear(), startMonth: Math.floor((m - 1) / 3) * 3 + 1 };
};
const lastQuarterStart = (today: Date) => {
  const { year, startMonth } = thisQuarterStart(today);
  let prevStart = startMonth - 3;
  let prevYear = year;
  if (prevStart < 1) { prevStart += 12; prevYear -= 1; }
  return { year: prevYear, startMonth: prevStart };
};
const quarterLabel = (year: number, startMonth: number, yearType: YearType) => {
  if (yearType === 'fiscal') {
    const fiscalQuarterMap: Record<number, number> = { 4: 1, 7: 2, 10: 3, 1: 4 };
    const fyStartYear = startMonth >= 4 ? year : year - 1;
    return `Q${fiscalQuarterMap[startMonth]} FY${String(fyStartYear).slice(2)}/${String(fyStartYear + 1).slice(2)}`;
  }
  return `Q${Math.floor(startMonth / 3) + 1} ${year}`;
};

// One shared label for both the KPI cards and the chart — 'this_year'/'last_year' read
// differently depending on whether they're calendar (Jan-Dec) or fiscal (Apr-Mar) years.
const periodSublabel = (period: PeriodType, yearType: YearType, customStart = '', customEnd = '') => {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth() + 1;
  if (period === 'month') return currentMonthLabel();
  if (period === 'last_month') return lastMonthLabel();
  if (period === 'all') return 'All time';
  if (period === 'custom') return formatCustomRangeLabel(customStart, customEnd);
  if (period === 'this_year') {
    if (yearType === 'fiscal') {
      const startYear = m >= 4 ? y : y - 1;
      return startYear === y ? `Apr–${MONTHS[m - 1]} ${y}` : `Apr ${startYear}–${MONTHS[m - 1]} ${y}`;
    }
    return `Jan–${MONTHS[m - 1]} ${y}`;
  }
  if (period === 'last_year') {
    if (yearType === 'fiscal') {
      const thisFiscalStartYear = m >= 4 ? y : y - 1;
      return fiscalYearLabel(thisFiscalStartYear - 1);
    }
    return `${y - 1}`;
  }
  if (period === 'this_quarter') {
    const { year, startMonth } = thisQuarterStart(today);
    return quarterLabel(year, startMonth, yearType);
  }
  // last_quarter
  const { year, startMonth } = lastQuarterStart(today);
  return quarterLabel(year, startMonth, yearType);
};

// Short name for the single period dropdown's trigger button/list rows — deliberately not the
// same string as periodSublabel above (that one spells out the actual dates, e.g. "Jan–Aug
// 2026"); this one is the plain option name, e.g. "This year".
const PERIOD_OPTION_LABELS: Record<PeriodType, string> = {
  month: 'This month',
  last_month: 'Last month',
  this_quarter: 'This quarter',
  last_quarter: 'Last quarter',
  this_year: 'This year',
  last_year: 'Last year',
  all: 'All time',
  custom: 'Custom range',
};

// Sequential (not order-of-operations) arithmetic, like a physical pocket calculator — matches
// what a user expects when quickly checking a sum, not a full expression evaluator.
const calcApply = (a: number, b: number, op: string): number => {
  switch (op) {
    case '+': return a + b;
    case '−': return a - b;
    case '×': return a * b;
    case '÷': return b === 0 ? NaN : a / b;
    default: return b;
  }
};
// Avoids floating-point display artifacts (e.g. 0.1 + 0.2 -> "0.30000000000000004").
const formatCalcValue = (n: number): string => (!isFinite(n) ? 'Error' : String(parseFloat(n.toFixed(10))));

// 3 rows of digits, each paired with the operator in its row's right-hand column —
// [['7','8','9'], op] etc. Rendered row-by-row below rather than a flat map, to keep the
// 4-column grid (3 digits + 1 operator per row) obviously correct rather than index-juggled.
const CALC_DIGIT_ROWS: Array<[string, string, string, string]> = [
  ['7', '8', '9', '÷'],
  ['4', '5', '6', '×'],
  ['1', '2', '3', '−'],
];

// Draws Income (up from zero)/Expenses (down from zero)/Net (line) bars positioned against a
// FIXED month-based x-axis — per direct request, the axis always shows the year's months
// regardless of granularity, and only the bars' own shape changes: one bar per month (Monthly),
// one wide bar spanning all 3 of a quarter's month-slots (Quarterly, using the backend's own
// pre-summed quarterly figures), or several thin bars packed into a single month's slot (Weekly,
// grouped by each week's month_label). Must render as a child of ChartsSurface (needs
// ChartsDataProvider's scale/drawing-area context). Hover reports back to the parent (via
// `onHover`) rather than using MUI's ChartsTooltip, since these bars aren't declared via
// BarPlot/series — the parent renders an HTML tooltip overlay positioned from the reported
// cursor coordinates (see the chart's render block below), so it works identically regardless
// of which granularity is currently drawn.
type HoveredBarInfo = { heading: string; income: number; expenses: number; net: number; vatOut: number; vatIn: number; clientX: number; clientY: number };

const TrendBars: React.FC<{
  monthlyTrend: MonthlyTrendPoint[];
  quarterlyTrend: MonthlyTrendPoint[];
  weeklyTrend: MonthlyTrendPoint[];
  dailyTrend: MonthlyTrendPoint[];
  granularity: ChartGranularity;
  onHover?: (info: HoveredBarInfo | null) => void;
}> = ({ monthlyTrend, quarterlyTrend, weeklyTrend, dailyTrend, granularity, onHover }) => {
  const scaleX = useXScale() as any;
  const scaleY = useYScale() as any;
  const { top: drawTop, height: drawHeight } = useDrawingArea();
  if (typeof scaleX !== 'function' || typeof scaleY !== 'function') return null;
  const bandwidth = typeof scaleX.bandwidth === 'function' ? scaleX.bandwidth() : 0;
  const zeroY = scaleY(0);

  type Bar = { x0: number; width: number; income: number; expenses: number; net: number; vatOut: number; vatIn: number; key: string; heading: string };
  let bars: Bar[] = [];

  if (granularity === 'quarterly') {
    for (let i = 0; i * 3 < monthlyTrend.length; i++) {
      const group = monthlyTrend.slice(i * 3, i * 3 + 3);
      const q = quarterlyTrend[i];
      if (!group.length || !q) continue;
      const x0 = scaleX(group[0].label);
      const xLast = scaleX(group[group.length - 1].label);
      if (x0 == null || xLast == null) continue;
      bars.push({
        x0, width: (xLast + bandwidth) - x0,
        income: Number(q.income), expenses: Number(q.expenses), net: Number(q.net),
        vatOut: Number(q.vat_out || 0), vatIn: Number(q.vat_in || 0),
        key: q.label, heading: `${q.label} (${q.months || ''})`,
      });
    }
  } else if (granularity === 'weekly' || granularity === 'daily') {
    // Daily reuses the exact same "subdivide this month's x-slot" approach weekly already uses
    // — a day, like a week, always belongs to exactly one month (see each point's own
    // `month_label`), so no new axis basis is needed, just a finer subdivision within it.
    const subTrend = granularity === 'weekly' ? weeklyTrend : dailyTrend;
    const byMonth = new Map<string, MonthlyTrendPoint[]>();
    subTrend.forEach(w => {
      const key = w.month_label || '';
      if (!byMonth.has(key)) byMonth.set(key, []);
      byMonth.get(key)!.push(w);
    });
    monthlyTrend.forEach((m) => {
      const items = byMonth.get(m.label) || [];
      const x0Month = scaleX(m.label);
      if (x0Month == null || items.length === 0) return;

      // Positioned within this month's own `bandwidth`. The xAxis itself sets
      // categoryGapRatio: 0 (see the ChartsDataProvider below), which removes the band scale's
      // inter-month gap entirely, so `bandwidth` already spans the *entire* distance to the next
      // month — meaning every day/week bar lands correctly under its own month's tick (which
      // ChartsXAxis centers under `bandwidth`) while still butting up snugly against the
      // neighboring month's bars, with no gap-vs-alignment tradeoff to make. (Before that xAxis
      // change, `bandwidth` was narrower than the month-to-month distance, forcing a choice
      // between a visible gap at every month boundary or late-month bars spilling into the next
      // month's own space — confirmed via a real browser reproduction where "31 Aug" rendered
      // solidly inside the gap before "Sep"'s own tick.)
      const [monAbbr, yearStr] = m.label.split(' ');
      const monthIndex = MONTHS.indexOf(monAbbr);
      const daysInMonth = monthIndex >= 0 && yearStr ? new Date(Number(yearStr), monthIndex + 1, 0).getDate() : items.length;

      if (granularity === 'daily') {
        const subWidth = bandwidth / daysInMonth;
        items.forEach(w => {
          const dayNum = parseInt(w.label, 10); // "31 Aug" -> 31
          const j = Number.isFinite(dayNum) ? dayNum - 1 : 0;
          bars.push({
            x0: x0Month + j * subWidth, width: subWidth,
            income: Number(w.income), expenses: Number(w.expenses), net: Number(w.net),
            vatOut: Number(w.vat_out || 0), vatIn: Number(w.vat_in || 0),
            key: `${m.label}-${j}`, heading: w.months || w.label,
          });
        });
      } else {
        // Weekly: positioned by each week's own sequential order within the month (array
        // index), NOT derived from its start day-of-month the way daily is above — weeks are
        // already non-overlapping backend buckets (see _week_list_from_range), but when a
        // period starts mid-week (e.g. "This quarter" starting on a Wednesday), the backend
        // clips the *first* week's start to that exact date rather than the preceding Monday,
        // while the second week still starts on the next real Monday — e.g. day 1 and day 6
        // both fall in a naive "day 1-7" bucket, floor-dividing to the *same* slot. That
        // produced two bars at the identical x position (and a duplicate React key), which is
        // exactly the stray vertical line: two points at the same x with different net values.
        // Array index has no such collision risk since it's just "the Nth week fetched for this
        // month", always unique and already in chronological order.
        const totalWeekSlots = Math.max(Math.ceil(daysInMonth / 7), items.length);
        const subWidth = bandwidth / totalWeekSlots;
        items.forEach((w, j) => {
          bars.push({
            x0: x0Month + j * subWidth, width: subWidth,
            income: Number(w.income), expenses: Number(w.expenses), net: Number(w.net),
            vatOut: Number(w.vat_out || 0), vatIn: Number(w.vat_in || 0),
            key: `${m.label}-${j}`, heading: w.months || w.label,
          });
        });
      }
    });
  } else {
    bars = monthlyTrend.map(m => {
      const x0 = scaleX(m.label);
      return x0 == null ? null : {
        x0, width: bandwidth,
        income: Number(m.income), expenses: Number(m.expenses), net: Number(m.net),
        vatOut: Number(m.vat_out || 0), vatIn: Number(m.vat_in || 0),
        key: m.label, heading: m.label,
      };
    }).filter((b): b is Bar => b !== null);
  }

  // Daily packs far more bars into the same month-slot than weekly does, so it gets an even
  // tighter gap ratio and smaller net-dots to stay legible rather than collapsing to slivers.
  const gapRatio = granularity === 'daily' ? 0.15 : granularity === 'weekly' ? 0.25 : 0.12;
  const netPoints = bars.map(b => ({ x: b.x0 + b.width / 2, y: scaleY(b.net) }));
  const linePath = netPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const netDotRadius = granularity === 'quarterly' ? 4 : granularity === 'weekly' ? 2 : granularity === 'daily' ? 1.5 : 3;

  return (
    <g>
      {bars.map(b => {
        const inset = b.width * gapRatio;
        const barX = b.x0 + inset;
        const barW = Math.max(b.width - inset * 2, 1);
        const incomeTop = scaleY(b.income);
        const incomeHeight = Math.max(zeroY - incomeTop, 0);
        const expensesBottom = scaleY(-b.expenses);
        const expensesHeight = Math.max(expensesBottom - zeroY, 0);
        const hoverInfo: Omit<HoveredBarInfo, 'clientX' | 'clientY'> = { heading: b.heading, income: b.income, expenses: b.expenses, net: b.net, vatOut: b.vatOut, vatIn: b.vatIn };
        const handleEnterOrMove = (e: React.MouseEvent) => onHover?.({ ...hoverInfo, clientX: e.clientX, clientY: e.clientY });
        const handleLeave = () => onHover?.(null);
        return (
          <g
            key={b.key}
            onMouseEnter={handleEnterOrMove}
            onMouseMove={handleEnterOrMove}
            onMouseLeave={handleLeave}
            style={{ cursor: 'pointer' }}
          >
            {/* Invisible full-height hit area — the visible income/expenses bars can be very
                thin (esp. in Weekly view) or entirely absent for a zero-value bar, which would
                otherwise make that bar impossible to hover reliably. */}
            <rect x={barX} y={drawTop} width={barW} height={drawHeight} fill="transparent" />
            <rect x={barX} y={incomeTop} width={barW} height={incomeHeight} fill={TREND_COLORS.income} rx={2} />
            <rect x={barX} y={zeroY} width={barW} height={expensesHeight} fill={TREND_COLORS.expenses} rx={2} />
          </g>
        );
      })}
      {netPoints.length > 0 && <path d={linePath} fill="none" stroke={TREND_COLORS.net} strokeWidth={2.5} />}
      {/* Dot size scales with granularity's own bar size: smallest for Weekly (thinnest bars),
          a bit bigger for Monthly, biggest for Quarterly (widest bars). */}
      {netPoints.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={netDotRadius} fill={TREND_COLORS.net} />
      ))}
    </g>
  );
};


// Renders the permanent "Q1 / Q2 / ..." label row centered under each group of 3 month-slots on
// the fixed month x-axis — shown once per quarter regardless of which granularity's bars are
// currently displayed (see TrendBars above), matching the same quarter grouping/labels. A small
// bracket (a horizontal line with tick marks at both ends) is drawn above each "Q1"/"Q2" label,
// spanning exactly the 3 months it covers — without it, "Q1"/"Q2"/etc. floating under the axis
// with no visual tie to which specific month labels they group was genuinely ambiguous (is Q1
// under Jan-Mar, or centered some other way?); the bracket makes the grouping unambiguous at a
// glance instead of requiring the reader to count/guess.
const QuarterAxisRow: React.FC<{ monthlyTrend: MonthlyTrendPoint[]; quarterlyTrend: MonthlyTrendPoint[] }> = ({ monthlyTrend, quarterlyTrend }) => {
  const { palette } = useTheme();
  const scaleX = useXScale() as any;
  const { top, height } = useDrawingArea();
  if (typeof scaleX !== 'function') return null;
  // Only meaningful once the axis spans more than a single quarter (This year/Last year, or a
  // wide custom range) — for a This quarter/This month period the axis is already 1-3 months
  // long, and a "Q3" bracket spanning the entire (already-quarter-scoped) axis would be
  // redundant at best, mislabeled at worst (e.g. a single month's axis has no real quarter
  // grouping to bracket).
  if (monthlyTrend.length < 6) return null;
  const bandwidth = typeof scaleX.bandwidth === 'function' ? scaleX.bandwidth() : 0;

  const groups: Array<{ xStart: number; xEnd: number; xCenter: number; label: string }> = [];
  for (let i = 0; i * 3 < monthlyTrend.length; i++) {
    const group = monthlyTrend.slice(i * 3, i * 3 + 3);
    const q = quarterlyTrend[i];
    if (!group.length || !q) continue;
    const x0 = scaleX(group[0].label);
    const xLast = scaleX(group[group.length - 1].label);
    if (x0 == null || xLast == null) continue;
    const xEnd = xLast + bandwidth;
    // "Q1" alone is enough here — the year is already implied by the month labels directly
    // above (and by the bracket only ever spanning the 3 months right below it), so repeating
    // "2026" on every single quarter label was pure clutter.
    groups.push({ xStart: x0, xEnd, xCenter: (x0 + xEnd) / 2, label: q.label.split(' ')[0] });
  }

  const bracketY = top + height + 22;
  const tickH = 4;
  const inset = 3;

  return (
    <g>
      {groups.map((g, i) => (
        <g key={`${g.label}-${i}`}>
          <line x1={g.xStart + inset} y1={bracketY} x2={g.xEnd - inset} y2={bracketY} stroke={palette.tokens.mutedSoft} strokeWidth={1} />
          <line x1={g.xStart + inset} y1={bracketY} x2={g.xStart + inset} y2={bracketY + tickH} stroke={palette.tokens.mutedSoft} strokeWidth={1} />
          <line x1={g.xEnd - inset} y1={bracketY} x2={g.xEnd - inset} y2={bracketY + tickH} stroke={palette.tokens.mutedSoft} strokeWidth={1} />
          <text x={g.xCenter} y={top + height + 35} textAnchor="middle" fontSize={10} fontWeight={700} fill={palette.tokens.mutedSoft}>
            {g.label}
          </text>
        </g>
      ))}
    </g>
  );
};


// Self-contained icon + popover — a minimal pocket calculator for quickly checking a sum next
// to a KPI card, not a full expression evaluator. Sequential arithmetic only (no precedence).
const CalculatorButton: React.FC = () => {
  const { palette } = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [display, setDisplay] = useState('0');
  const [prevValue, setPrevValue] = useState<number | null>(null);
  const [operator, setOperator] = useState<string | null>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  // Set only by '=', shows the full sum just computed (e.g. "32,900 + 14,055 =") above the
  // result — cleared the moment any new key is pressed, since it's then stale history.
  const [lastEquation, setLastEquation] = useState('');

  const reset = useCallback(() => {
    setDisplay('0'); setPrevValue(null); setOperator(null); setWaitingForOperand(false); setLastEquation('');
  }, []);

  const inputDigit = useCallback((d: string) => {
    setLastEquation('');
    if (waitingForOperand) { setDisplay(d); setWaitingForOperand(false); }
    else setDisplay(display === '0' ? d : display + d);
  }, [display, waitingForOperand]);

  const inputDecimal = useCallback(() => {
    setLastEquation('');
    if (waitingForOperand) { setDisplay('0.'); setWaitingForOperand(false); return; }
    if (!display.includes('.')) setDisplay(display + '.');
  }, [display, waitingForOperand]);

  const backspace = useCallback(() => {
    if (waitingForOperand) return;
    setLastEquation('');
    setDisplay(display.length > 1 ? display.slice(0, -1) : '0');
  }, [display, waitingForOperand]);

  const handleOperator = useCallback((nextOp: string) => {
    setLastEquation('');
    const inputValue = parseFloat(display);
    if (prevValue == null) {
      setPrevValue(inputValue);
    } else if (operator && !waitingForOperand) {
      const result = calcApply(prevValue, inputValue, operator);
      setPrevValue(result);
      setDisplay(formatCalcValue(result));
    }
    setOperator(nextOp);
    setWaitingForOperand(true);
  }, [display, prevValue, operator, waitingForOperand]);

  const handleEquals = useCallback(() => {
    const inputValue = parseFloat(display);
    if (operator && prevValue != null) {
      setLastEquation(`${formatCalcValue(prevValue)} ${operator} ${formatCalcValue(inputValue)} =`);
      setDisplay(formatCalcValue(calcApply(prevValue, inputValue, operator)));
      setPrevValue(null);
      setOperator(null);
      setWaitingForOperand(true);
    }
  }, [display, prevValue, operator]);

  // Keyboard input while the popover is open — global listener since MUI renders Popover
  // content in a portal, so a plain onKeyDown on the content box can't reliably catch keys
  // typed before anything inside it has focus.
  useEffect(() => {
    if (!anchorEl) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') { inputDigit(e.key); e.preventDefault(); }
      else if (e.key === '.') { inputDecimal(); e.preventDefault(); }
      else if (e.key === '+') { handleOperator('+'); e.preventDefault(); }
      else if (e.key === '-') { handleOperator('−'); e.preventDefault(); }
      else if (e.key === '*') { handleOperator('×'); e.preventDefault(); }
      else if (e.key === '/') { handleOperator('÷'); e.preventDefault(); }
      else if (e.key === 'Enter' || e.key === '=') { handleEquals(); e.preventDefault(); }
      else if (e.key === 'Backspace') { backspace(); e.preventDefault(); }
      else if (e.key === 'Escape' || e.key === 'c' || e.key === 'C') { reset(); e.preventDefault(); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [anchorEl, inputDigit, inputDecimal, backspace, handleOperator, handleEquals, reset]);

  // Derived expression line: the running sum being built, e.g. "32,900 +" while waiting for
  // the second operand, or "32,900 + 14,055" while typing it — falls back to the just-computed
  // equation (lastEquation) once '=' has been pressed.
  const expressionLine = lastEquation || (operator && prevValue != null
    ? `${formatCalcValue(prevValue)} ${operator}${waitingForOperand ? '' : ` ${display}`}`
    : '');

  const keyButtonSx = { minWidth: 0, borderColor: palette.tokens.hairline, color: palette.tokens.ink, fontWeight: 600 };

  return (
    <>
      <Tooltip title="Quick calculator">
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ color: palette.tokens.mutedSoft }}>
          <CalculateIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 1.5, width: 216 }}>
          <Box sx={{
            textAlign: 'right', px: 1.5, py: 1, mb: 1, bgcolor: palette.tokens.ink, color: palette.tokens.white,
            borderRadius: 1, overflow: 'hidden',
          }}>
            <Typography sx={{
              fontSize: 12, color: palette.tokens.mutedSoft, fontFamily: 'monospace', lineHeight: 1.4,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minHeight: 16,
            }}>
              {expressionLine}
            </Typography>
            <Typography sx={{
              fontSize: 20, fontWeight: 700, fontFamily: 'monospace',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {display}
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5 }}>
            <Button size="small" variant="outlined" onClick={reset} sx={{ ...keyButtonSx, color: palette.tokens.error }}>C</Button>
            <Button size="small" variant="outlined" onClick={backspace} sx={keyButtonSx}><BackspaceIcon sx={{ fontSize: 15 }} /></Button>
            <Box />
            <Button size="small" variant="contained" onClick={() => handleOperator('+')} sx={{ minWidth: 0, fontWeight: 700 }}>+</Button>

            {CALC_DIGIT_ROWS.map(([a, b, c, op]) => (
              <React.Fragment key={op}>
                <Button size="small" variant="outlined" onClick={() => inputDigit(a)} sx={keyButtonSx}>{a}</Button>
                <Button size="small" variant="outlined" onClick={() => inputDigit(b)} sx={keyButtonSx}>{b}</Button>
                <Button size="small" variant="outlined" onClick={() => inputDigit(c)} sx={keyButtonSx}>{c}</Button>
                <Button size="small" variant="outlined" onClick={() => handleOperator(op)} sx={keyButtonSx}>{op}</Button>
              </React.Fragment>
            ))}

            <Button size="small" variant="outlined" onClick={() => inputDigit('0')} sx={{ ...keyButtonSx, gridColumn: 'span 2' }}>0</Button>
            <Button size="small" variant="outlined" onClick={inputDecimal} sx={keyButtonSx}>.</Button>
            <Button size="small" variant="contained" onClick={handleEquals} sx={{ minWidth: 0, fontWeight: 700 }}>=</Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

// One bank connection's row — shared between the "Your banks" sidebar panel (capped at
// BANK_PANEL_CAP, see below) and its "All banks" overflow dialog, so the two can't drift apart.
const BANK_PANEL_CAP = 2;

interface BankConnectionRowProps {
  conn: BankConnection;
  owners: Owner[];
  syncing: boolean;
  connecting: boolean;
  onSetOwner: (conn: BankConnection, ownerId: string | null) => void;
  onResync: (conn: BankConnection) => void;
  onDisconnect: (conn: BankConnection) => void;
  onReconnect: () => void;
}

const BankConnectionRow: React.FC<BankConnectionRowProps> = ({ conn, owners, syncing, connecting, onSetOwner, onResync, onDisconnect, onReconnect }) => {
  const { palette } = useTheme();
  const needsReauth = conn.status === 'needs_reauth';
  return (
    <Box sx={{ border: `1px solid ${palette.tokens.hairline}`, borderRadius: 2, p: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title={needsReauth ? 'This connection has expired — reconnect to keep syncing' : 'Connected'}>
          <Box sx={{ position: 'relative', width: 8, height: 8, flexShrink: 0 }}>
            {!needsReauth && (
              <Box sx={{
                position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: palette.tokens.success,
                animation: 'pulse-dot 2s ease-out infinite',
                '@keyframes pulse-dot': {
                  '0%': { transform: 'scale(1)', opacity: 0.35 },
                  '70%': { transform: 'scale(2.2)', opacity: 0 },
                  '100%': { transform: 'scale(2.2)', opacity: 0 },
                },
              }} />
            )}
            <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: needsReauth ? palette.tokens.error : palette.tokens.success }} />
          </Box>
        </Tooltip>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: palette.tokens.ink, fontSize: 13 }} noWrap>
            {prettyInstitutionName(conn.institution_name)}
            {conn.account_mask && <Box component="span" sx={{ color: palette.tokens.mutedSoft, fontWeight: 500 }}> ····{conn.account_mask}</Box>}
          </Typography>
        </Box>
        {owners.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 90, maxWidth: 140, flexShrink: 0 }}>
            <Select
              value={conn.owner_id || ''}
              displayEmpty
              onChange={e => onSetOwner(conn, e.target.value || null)}
              sx={{
                fontSize: 11, height: 28,
                '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
              }}
            >
              <MenuItem value="">Unassigned</MenuItem>
              {owners.map(o => (
                <MenuItem key={o.id} value={o.id} sx={{ maxWidth: 220 }}>
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {o.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {needsReauth && (
            <Tooltip title="Reconnect this bank">
              <IconButton size="small" disabled={connecting} onClick={onReconnect} sx={{ color: palette.tokens.error }}>
                <WarningIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Resync full 365-day history — use this if older transactions seem to be missing">
            <IconButton size="small" disabled={syncing} onClick={() => onResync(conn)} sx={{ color: palette.tokens.muted }}>
              {syncing ? <CircularProgress size={14} /> : <ResyncIcon sx={{ fontSize: 15 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Disconnect">
            <IconButton size="small" onClick={() => onDisconnect(conn)} sx={{ color: palette.tokens.muted, '&:hover': { color: palette.tokens.error } }}>
              <DisconnectIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

// ── main component ─────────────────────────────────────────────────────────────

const AccountingPage: React.FC = () => {
  const { palette } = useTheme();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState<PeriodType>(() => readAccountingLS(ACCOUNTING_LS_KEYS.period, 'this_year' as PeriodType));
  // Calendar (Jan-Dec) vs fiscal (Apr-Mar, UK tax year) — its own small dropdown next to the
  // Accounts selector; shared by both the KPI cards and the Income vs Expenses chart below (one
  // control, not two).
  const [yearType, setYearType] = useState<YearType>(() => readAccountingLS(ACCOUNTING_LS_KEYS.yearType, 'calendar' as YearType));
  const [yearTypeMenuAnchorEl, setYearTypeMenuAnchorEl] = useState<HTMLElement | null>(null);
  // One period dropdown (This month/This quarter/This year always visible, "More ranges"
  // expands in place for Last month/Last quarter/Last year/All time/Custom range) instead of 3
  // pill buttons plus a separate "More ranges" trigger.
  const [periodMenuAnchorEl, setPeriodMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [moreRangesExpanded, setMoreRangesExpanded] = useState(false);
  // "View" dropdown (Quarterly/Monthly/Weekly/Daily) — same Button+Popover pattern as
  // Source/Year/Range, a 4th filter column rather than its own separate control elsewhere.
  const [granularityMenuAnchorEl, setGranularityMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [showCustomInputs, setShowCustomInputs] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  // Only commits to `period: 'custom'` once Apply is pressed, from a value known to be valid —
  // avoids firing off fetches on every keystroke while typing/picking a date.
  const [appliedCustomStart, setAppliedCustomStart] = useState(() => readAccountingLS(ACCOUNTING_LS_KEYS.customStart, ''));
  const [appliedCustomEnd, setAppliedCustomEnd] = useState(() => readAccountingLS(ACCOUNTING_LS_KEYS.customEnd, ''));
  // Entity/"book" selector — scopes every section on this page (KPIs, chart, bank connections,
  // bank statements, invoices/receipts) to one Owner record (a person or business) via an
  // owner_id query param. null = unscoped "All", identical to this page's pre-existing behavior.
  const [owners, setOwners] = useState<Owner[]>([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string | null>(() => readAccountingLS(ACCOUNTING_LS_KEYS.ownerId, null as string | null));
  const [bankTxnDialogOpen, setBankTxnDialogOpen] = useState(false);
  const [addTransactionDialogOpen, setAddTransactionDialogOpen] = useState(false);
  const [statementsListDialogOpen, setStatementsListDialogOpen] = useState(false);
  // Which tab the "Receipts & invoices" section shows — same pseudo-tab reveal pattern as
  // PropertiesPage.tsx's flat tabs/the certificates dashboard (one tab's cards visible at a
  // time, auto-width tabs, no split-evenly/pink-outer styling — that's specific to the "Your
  // banks" panel above, not this one).
  const [invoiceTab, setInvoiceTab] = useState<'receipts' | 'invoices'>('receipts');
  // Reconciliation: attaching a receipt/invoice as "proof" of a specific bank transaction (live
  // feed or an uploaded statement row) — see UploadProofDialog/ProofViewDialog.
  const [uploadProofTarget, setUploadProofTarget] = useState<{ bank_transaction_id?: string; bank_statement_transaction_id?: string } | null>(null);
  const [viewProofTarget, setViewProofTarget] = useState<InvoiceRecord | null>(null);
  const [summary, setSummary] = useState<Summary>({
    collected: '0', outstanding: '0', deposits_held: '0',
    total_spent: '0', total_spent_invoices: '0', total_spent_receipts: '0', total_spent_statements: '0',
    expenses_general: '0',
    bank_income: '0',
    vat_out: '0', vat_in: '0',
  });
  const [loading, setLoading] = useState(true);
  const [monthlyTrend, setMonthlyTrend] = useState<MonthlyTrendPoint[]>(() => cachedTrendData?.monthly || []);
  // The chart's x-axis is always "months of the year" regardless of which granularity is
  // selected (see the chart render below) — quarterlyTrend/weeklyTrend are fetched alongside
  // monthlyTrend (quarterly always, for the permanent Q1/Q2/... label row under the axis;
  // weekly only when actually selected) and positioned within that same fixed month axis rather
  // than driving their own separate axis.
  const [quarterlyTrend, setQuarterlyTrend] = useState<MonthlyTrendPoint[]>(() => cachedTrendData?.quarterly || []);
  const [weeklyTrend, setWeeklyTrend] = useState<MonthlyTrendPoint[]>(() => cachedTrendData?.weekly || []);
  const [dailyTrend, setDailyTrend] = useState<MonthlyTrendPoint[]>(() => cachedTrendData?.daily || []);
  const [monthlyTrendLoading, setMonthlyTrendLoading] = useState(() => !cachedTrendData);
  // Which granularities are actually offered depends on the selected period — see
  // GRANULARITY_OPTIONS_BY_PERIOD (This year offers Monthly/Quarterly/Weekly, This quarter adds
  // Daily, This month offers only Weekly/Daily, etc).
  const [chartGranularity, setChartGranularity] = useState<ChartGranularity>(() => readAccountingLS(ACCOUNTING_LS_KEYS.chartGranularity, 'monthly' as ChartGranularity));
  // Which bank data feeds BOTH the KPI cards and the chart's Income/Expenses — a single shared
  // account-scope selection (see the top toolbar's "Accounts" picker), not two independently-
  // configured ones, so the cards and chart can never disagree. The live TrueLayer feed and
  // uploaded statement PDFs are treated as mutually exclusive sources (never blended — see
  // backend/routes/accounting.py's bank_source docs), so this picks one, not both.
  // connectionIds is only meaningful when bankSource === 'connections' and lets the user narrow
  // to one specific connected bank, the other, or both (default/null: all).
  const [bankSource, setBankSource] = useState<'connections' | 'statements'>(() => readAccountingLS(ACCOUNTING_LS_KEYS.bankSource, 'connections' as 'connections' | 'statements'));
  const [connectionIds, setConnectionIds] = useState<string[] | null>(() => readAccountingLS(ACCOUNTING_LS_KEYS.connectionIds, null as string[] | null));
  // "Manual entry" (self-reported cash transactions — see cashTransactionRows) is NOT a third
  // option alongside bankSource's 'connections'/'statements' — it's additive with whichever of
  // those is picked, since a manual entry isn't a real bank connection or statement at all. Off
  // by default: turning it on changes the KPI/chart totals, so it should be an explicit opt-in
  // rather than something that silently shifts numbers the first time this page loads.
  const [includeManualEntries, setIncludeManualEntries] = useState<boolean>(() => readAccountingLS(ACCOUNTING_LS_KEYS.includeManualEntries, false));
  const [accountsMenuAnchorEl, setAccountsMenuAnchorEl] = useState<HTMLElement | null>(null);
  // Chart hover tooltip — the bars themselves are hand-drawn SVG (see TrendBars), not declared
  // via MUI's BarPlot/series, so there's no ChartsTooltip to lean on; TrendBars reports the
  // hovered bar's figures + cursor position back up here, and this renders a small HTML overlay
  // positioned relative to `chartAreaRef`. Content is naturally dynamic per granularity since
  // TrendBars' own `heading` string already differs for Monthly/Quarterly/Weekly bars.
  const [hoveredBar, setHoveredBar] = useState<HoveredBarInfo & { x: number; y: number } | null>(null);
  const chartAreaRef = useRef<HTMLDivElement | null>(null);
  const handleBarHover = useCallback((info: HoveredBarInfo | null) => {
    if (!info) { setHoveredBar(null); return; }
    const rect = chartAreaRef.current?.getBoundingClientRect();
    setHoveredBar({ ...info, x: rect ? info.clientX - rect.left : info.clientX, y: rect ? info.clientY - rect.top : info.clientY });
  }, []);

  // Keep every persisted filter/view choice above in sync with localStorage as it changes, so
  // navigating away from this page (which fully unmounts it, per the react-router routes in
  // App.tsx) and back restores exactly what was selected rather than resetting to defaults.
  useEffect(() => { writeAccountingLS(ACCOUNTING_LS_KEYS.period, period); }, [period]);
  useEffect(() => { writeAccountingLS(ACCOUNTING_LS_KEYS.yearType, yearType); }, [yearType]);
  useEffect(() => { writeAccountingLS(ACCOUNTING_LS_KEYS.ownerId, selectedOwnerId); }, [selectedOwnerId]);
  useEffect(() => { writeAccountingLS(ACCOUNTING_LS_KEYS.customStart, appliedCustomStart); }, [appliedCustomStart]);
  useEffect(() => { writeAccountingLS(ACCOUNTING_LS_KEYS.customEnd, appliedCustomEnd); }, [appliedCustomEnd]);
  useEffect(() => { writeAccountingLS(ACCOUNTING_LS_KEYS.chartGranularity, chartGranularity); }, [chartGranularity]);
  useEffect(() => { setHoveredBar(null); }, [chartGranularity]);
  // The chart's time span now follows `period` (see GRANULARITY_OPTIONS_BY_PERIOD) — whenever
  // period changes, snap chartGranularity to that period's default if the persisted/previous
  // selection isn't one of its options (e.g. 'daily' carried over from This quarter is not a
  // valid choice once the period switches to This year). Also re-checked whenever the applied
  // custom range itself changes, since 'daily' can become invalid for 'custom' without `period`
  // changing at all (see granularityOptionsFor/MAX_DAILY_SPAN_DAYS) — e.g. widening an existing
  // custom range past 100 days while it's already selected.
  useEffect(() => {
    const allowed = granularityOptionsFor(period, appliedCustomStart, appliedCustomEnd).options.map(o => o.key);
    if (!allowed.includes(chartGranularity)) {
      setChartGranularity(granularityOptionsFor(period, appliedCustomStart, appliedCustomEnd).default);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, appliedCustomStart, appliedCustomEnd]);
  useEffect(() => { writeAccountingLS(ACCOUNTING_LS_KEYS.bankSource, bankSource); }, [bankSource]);
  useEffect(() => { writeAccountingLS(ACCOUNTING_LS_KEYS.connectionIds, connectionIds); }, [connectionIds]);
  useEffect(() => { writeAccountingLS(ACCOUNTING_LS_KEYS.includeManualEntries, includeManualEntries); }, [includeManualEntries]);

  // bank connection (TrueLayer POC) state
  const [bankConnections, setBankConnections] = useState<BankConnection[]>([]);
  const [bankTransactions, setBankTransactions] = useState<BankTransactionRow[]>([]);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // Surfaced inline on the "Your banks" panel — a sync failure used to be swallowed entirely
  // (fetch response was never checked), so a stale/revoked connection just looked like it worked.
  const [bankSyncError, setBankSyncError] = useState<string | null>(null);
  const [disconnectTarget, setDisconnectTarget] = useState<BankConnection | null>(null);
  const [disconnectAllConfirmOpen, setDisconnectAllConfirmOpen] = useState(false);
  // "See more" overflow — the compact sidebar panel caps at BANK_PANEL_CAP connection rows so it
  // never stretches the welcome-hero row taller as banks are added (same pattern as
  // PropertiesPage.tsx's owners sidebar / OWNER_PANEL_CAP).
  const [banksOverflowOpen, setBanksOverflowOpen] = useState(false);
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const [acceptingConsent, setAcceptingConsent] = useState(false);
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState<string | null>(null);
  // Inline "Set up 2FA" flow, opened in place of navigating away to /profile when
  // handleConnectBank's mfa_enrollment_required response comes back — same QR/confirm steps as
  // ProfilePage.tsx's Two-Factor Authentication card, just surfaced right here so connecting a
  // bank doesn't require leaving this page first.
  const [mfaEnrollDialogOpen, setMfaEnrollDialogOpen] = useState(false);
  const [mfaEnrolling, setMfaEnrolling] = useState(false);
  const [mfaQrCode, setMfaQrCode] = useState<string | null>(null);
  const [mfaSecret, setMfaSecret] = useState<string | null>(null);
  const [mfaConfirmCode, setMfaConfirmCode] = useState('');
  const [mfaConfirming, setMfaConfirming] = useState(false);
  const [mfaEnrollError, setMfaEnrollError] = useState<string | null>(null);

  // bank statements (uploaded PDF -> AI-extracted digital record) state
  const [bankStatements, setBankStatements] = useState<BankStatementRecord[]>([]);
  const [bankStatementsLoading, setBankStatementsLoading] = useState(true);
  const [bankStatementUploadOpen, setBankStatementUploadOpen] = useState(false);
  const [expandedStatementId, setExpandedStatementId] = useState<string | null>(null);
  const [statementDetail, setStatementDetail] = useState<Record<string, BankStatementTransaction[]>>({});
  const [loadingStatementDetail, setLoadingStatementDetail] = useState<string | null>(null);

  // Which tab the "Your banks"/"Your statements" sidebar panel itself is showing — purely a
  // display choice for that one card (which list/actions it renders), deliberately independent
  // of bankSource below (the "Accounts" filter Popover's own Source toggle), which is what
  // actually scopes the KPI cards/chart. Switching this tab must never silently change what the
  // numbers above are counting.
  const [bankPanelSource, setBankPanelSource] = useState<'connections' | 'statements'>('connections');
  const [deleteStatementTarget, setDeleteStatementTarget] = useState<string | null>(null);

  // invoices (upload + auto-sort) state
  const [allInvoiceRecords, setAllInvoiceRecords] = useState<InvoiceRecord[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoiceUploadOpen, setInvoiceUploadOpen] = useState(false);
  const [receiptUploadOpen, setReceiptUploadOpen] = useState(false);
  // "Add all" bulk action next to each tab's Upload button — reopens BulkInvoiceReviewDialog for
  // every not-yet-added invoice/receipt of whichever type was clicked, same as the review screen
  // shown right after a bulk upload. null = closed.
  const [bulkAddDocType, setBulkAddDocType] = useState<'invoices' | 'receipts' | null>(null);
  const [deleteInvoiceTarget, setDeleteInvoiceTarget] = useState<string | null>(null);
  const [deletingInvoice, setDeletingInvoice] = useState(false);
  // Newly-uploaded (or previously-dismissed, revisited from the "Pending confirmation" section)
  // receipts/invoices awaiting a "new expense, or proof of an existing transaction?" decision —
  // see Invoice.confirmed. Processed one at a time; ConfirmNewExpenseDialog always shows [0].
  const [pendingConfirmQueue, setPendingConfirmQueue] = useState<PendingInvoice[]>([]);
  const [deleteInvoiceError, setDeleteInvoiceError] = useState<string | null>(null);

  const headers = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  // owner_id query-string fragment shared by every fetch below — '' when "All" is selected, so
  // appending it is a no-op and every endpoint's default (unscoped) behavior is unchanged.
  const ownerParam = selectedOwnerId ? `&owner_id=${selectedOwnerId}` : '';

  const fetchOwners = useCallback(async () => {
    if (!token) return;
    const res = await fetch(`${config.apiUrl}/api/accounting/owners`, { headers: headers() });
    if (res.ok) {
      const d = await res.json();
      setOwners((d.owners || []).filter((o: Owner) => o.is_owner));
    }
  }, [token, headers]);

  useEffect(() => { fetchOwners(); }, [fetchOwners]);

  // `silent` skips the page-wide loading spinner — used for refreshes after an in-place action
  // (save, delete) so the KPI cards don't unmount/flash while the request is in flight.
  const fetchData = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setLoading(true);
    try {
      const customParams = period === 'custom' && appliedCustomStart && appliedCustomEnd
        ? `&start_date=${appliedCustomStart}&end_date=${appliedCustomEnd}` : '';
      const connectionIdsParam = connectionIds && connectionIds.length > 0
        ? `&connection_ids=${connectionIds.join(',')}` : '';
      const bankSourceParams = `&bank_source=${bankSource}${bankSource === 'connections' ? connectionIdsParam : ''}${includeManualEntries ? '&include_manual=true' : ''}`;
      const sumRes = await fetch(`${config.apiUrl}/api/accounting/summary?period=${period}&year_type=${yearType}${customParams}${ownerParam}${bankSourceParams}`, { headers: headers() });
      if (sumRes.ok) {
        const d = await sumRes.json();
        setSummary(d.summary);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token, period, yearType, appliedCustomStart, appliedCustomEnd, headers, ownerParam, bankSource, connectionIds, includeManualEntries]);

  // fetchData's identity changes whenever `period` changes, re-running this effect — only the
  // very first call (nothing loaded yet) should show the spinner; period-toggle refreshes after
  // that should update in place, same as the mark-paid/save/delete silent-refresh pattern above.
  const hasLoadedRentData = useRef(false);
  useEffect(() => {
    fetchData(hasLoadedRentData.current);
    hasLoadedRentData.current = true;
  }, [fetchData]);

  // ── bank connections (TrueLayer POC) ────────────────────────────────────────

  const fetchBankData = useCallback(async () => {
    if (!token) return;
    const trueLayerRes = await fetch(`${config.apiUrl}/api/truelayer/connections?${ownerParam.slice(1)}`, { headers: headers() });
    const trueLayerData = trueLayerRes.ok ? await trueLayerRes.json() : { connections: [] };
    const connections: BankConnection[] = trueLayerData.connections || [];
    setBankConnections(connections);

    if (connections.length > 0) {
      // A connection's owner is already fixed by its own owner_id (set via the assign dropdown
      // below) — no need to pass ownerParam here, it's inherited from the (now-scoped) list above.
      // Fetch every connection's transactions in parallel and merge them into one list — a user
      // with two banks (e.g. Revolut + Starling) needs both represented in the chart AND the
      // table, not just whichever connection happened to be first in the list.
      const results = await Promise.all(connections.map(conn =>
        fetch(`${config.apiUrl}/api/${conn.provider}/connections/${conn.id}/transactions`, { headers: headers() })
          .then(res => res.ok ? res.json() : { transactions: [] })
          .catch(() => ({ transactions: [] }))
      ));
      const merged = results.flatMap(r => r.transactions || []);
      merged.sort((a, b) => b.date.localeCompare(a.date));
      setBankTransactions(merged);
    } else {
      setBankTransactions([]);
    }
  }, [token, headers, ownerParam]);

  useEffect(() => { fetchBankData(); }, [fetchBankData]);

  // Default the shared account-scope's bank source to whichever system actually has data — a
  // fresh account with a statement uploaded but no live connection shouldn't default to an
  // empty "Bank connections" view. Only runs once (per mount); a user's own later choice is
  // never overridden.
  const hasSetDefaultBankSource = useRef(false);
  useEffect(() => {
    if (hasSetDefaultBankSource.current || bankStatementsLoading) return;
    hasSetDefaultBankSource.current = true;
    if (bankConnections.length === 0 && bankStatements.length > 0) {
      setBankSource('statements');
    }
  }, [bankConnections.length, bankStatements.length, bankStatementsLoading]);

  // Refresh the connections list once a TrueLayer connect tab reports completion (postMessage),
  // with a focus-listener fallback for cases where postMessage doesn't fire (e.g. popup blocked
  // into a same-tab navigation instead of a new tab). The KPI cards/chart also need refreshing
  // here (added below, once fetchMonthlyTrend exists) since a newly-connected bank's initial sync
  // happens entirely server-side.

  // ── bank statements (uploaded PDF -> AI-extracted digital record) ──────────
  // Deliberately separate from fetchBankData above — that's the live TrueLayer feed,
  // this is manually uploaded historical/standalone statement PDFs (see routes/bank_statements.py).

  const fetchBankStatements = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setBankStatementsLoading(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/bank-statements?${ownerParam.slice(1)}`, { headers: headers() });
      if (res.ok) {
        const d = await res.json();
        setBankStatements(d.statements || []);
      }
    } finally {
      if (!silent) setBankStatementsLoading(false);
    }
  }, [token, headers, ownerParam]);

  useEffect(() => { fetchBankStatements(); }, [fetchBankStatements]);

  const openStatementDetail = async (statementId: string) => {
    setExpandedStatementId(statementId);
    if (!statementDetail[statementId]) {
      setLoadingStatementDetail(statementId);
      try {
        const res = await fetch(`${config.apiUrl}/api/bank-statements/${statementId}`, { headers: headers() });
        if (res.ok) {
          const d = await res.json();
          setStatementDetail(prev => ({ ...prev, [statementId]: d.statement.transactions || [] }));
        }
      } finally {
        setLoadingStatementDetail(null);
      }
    }
  };

  // Unlike openStatementDetail above, always refetches — used after a proof is attached/unlinked/
  // deleted while this statement's detail dialog is open, since the cached copy is now stale.
  const refetchStatementDetail = useCallback(async (statementId: string) => {
    const res = await fetch(`${config.apiUrl}/api/bank-statements/${statementId}`, { headers: headers() });
    if (res.ok) {
      const d = await res.json();
      setStatementDetail(prev => ({ ...prev, [statementId]: d.statement.transactions || [] }));
    }
  }, [headers]);

  const handleDeleteStatement = async () => {
    if (!deleteStatementTarget) return;
    await fetch(`${config.apiUrl}/api/bank-statements/${deleteStatementTarget}`, { method: 'DELETE', headers: headers() });
    setDeleteStatementTarget(null);
    if (expandedStatementId === deleteStatementTarget) setExpandedStatementId(null);
    fetchBankStatements(true);
    // A deleted statement's transactions no longer count toward Income/Expenses — refresh both
    // the KPI cards and the trend chart, same as every other mutation that changes their inputs.
    fetchData(true);
    fetchMonthlyTrend(true);
  };

  // ── invoices (upload + auto-sort) ───────────────────────────────────────────

  // `silent` skips the invoices-section spinner — used for background refreshes (item toggle,
  // upload, delete) so the grid doesn't flash while only the totals actually changed.
  const fetchInvoices = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setInvoicesLoading(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/invoices?${ownerParam.slice(1)}`, { headers: headers() });
      if (res.ok) {
        const d = await res.json();
        setAllInvoiceRecords(d.invoices || []);
      }
    } finally {
      if (!silent) setInvoicesLoading(false);
    }
  }, [token, headers, ownerParam]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // Income vs Expenses chart — 'income' means incoming bank transactions (live feed or uploaded
  // statements). Now driven by the shared period control (This month/This quarter/This year/...)
  // exactly like the KPI cards, via the same period/year_type/custom-date vocabulary as fetchData
  // above — selecting This quarter focuses the chart on that quarter's months, This month on that
  // month's days, etc. (see GRANULARITY_OPTIONS_BY_PERIOD for which views each period offers).
  //
  // Always fetches monthly (the axis is permanently "months of the resolved period" regardless
  // of which granularity's bars are shown — see the chart render below). Quarterly is fetched
  // whenever it's the selected granularity OR the period is a full year (so the Q1/Q2/... bracket
  // row can render — see QuarterAxisRow); weekly/daily are only fetched when actually selected,
  // avoiding an unused fetch for a granularity most views of this chart won't use.
  //
  // `silent` skips the loading spinner — used when a mutation (invoice/receipt change) should
  // update the chart in place rather than flash it, same pattern as fetchData/fetchInvoices below.
  const fetchMonthlyTrend = useCallback((silent = false) => {
    if (!token) return;
    if (!silent) setMonthlyTrendLoading(true);
    const customParams = period === 'custom' && appliedCustomStart && appliedCustomEnd
      ? `&start_date=${appliedCustomStart}&end_date=${appliedCustomEnd}` : '';
    const connectionIdsParam = connectionIds && connectionIds.length > 0
      ? `&connection_ids=${connectionIds.join(',')}` : '';
    const bankSourceParams = `&bank_source=${bankSource}${bankSource === 'connections' ? connectionIdsParam : ''}${includeManualEntries ? '&include_manual=true' : ''}`;
    const base = `${config.apiUrl}/api/accounting/monthly-trend?period=${period}&year_type=${yearType}&income_source=bank${customParams}${ownerParam}${bankSourceParams}`;
    const fetchGranularity = (g: string) => fetch(`${base}&granularity=${g}`, { headers: headers() }).then(res => res.ok ? res.json() : null);
    const isYearlyPeriod = period === 'this_year' || period === 'last_year';
    const needsQuarterly = chartGranularity === 'quarterly' || isYearlyPeriod;
    Promise.all([
      fetchGranularity('monthly'),
      needsQuarterly ? fetchGranularity('quarterly') : Promise.resolve(null),
      chartGranularity === 'weekly' ? fetchGranularity('weekly') : Promise.resolve(null),
      chartGranularity === 'daily' ? fetchGranularity('daily') : Promise.resolve(null),
    ])
      .then(([m, q, w, d]) => {
        if (m) setMonthlyTrend(m.months || []);
        if (q) setQuarterlyTrend(q.months || []);
        if (w) setWeeklyTrend(w.months || []);
        if (d) setDailyTrend(d.months || []);
        // Keep the module-level cache in sync so the next unmount/remount of this page (see
        // cachedTrendData above) can paint immediately instead of flashing a spinner first.
        cachedTrendData = {
          monthly: m ? (m.months || []) : (cachedTrendData?.monthly || []),
          quarterly: q ? (q.months || []) : (cachedTrendData?.quarterly || []),
          weekly: w ? (w.months || []) : (cachedTrendData?.weekly || []),
          daily: d ? (d.months || []) : (cachedTrendData?.daily || []),
        };
      })
      .finally(() => setMonthlyTrendLoading(false));
  }, [token, period, yearType, appliedCustomStart, appliedCustomEnd, chartGranularity, headers, ownerParam, bankSource, connectionIds, includeManualEntries]);

  // Only the very first load shows the spinner — switching granularity (or period/owner/bank
  // scope) re-runs this same effect, but should update the chart in place rather than blank it
  // out and re-flash it, especially since the x-axis itself (months) usually doesn't even
  // change between granularities, same silent-refresh pattern as fetchData's hasLoadedRentData.
  // Seeded from cachedTrendData too — a remount that already has cached data to paint should
  // treat its first fetch as a silent refresh, not a spinner-first load.
  const hasLoadedTrendOnce = useRef(!!cachedTrendData);
  useEffect(() => {
    fetchMonthlyTrend(hasLoadedTrendOnce.current);
    hasLoadedTrendOnce.current = true;
  }, [fetchMonthlyTrend]);

  // Refresh the connections list once a TrueLayer connect tab reports completion (postMessage),
  // with a focus-listener fallback for cases where postMessage doesn't fire (e.g. popup blocked
  // into a same-tab navigation instead of a new tab). Also refreshes the KPI cards/chart, since a
  // newly-connected bank's initial sync happens entirely server-side — without this they'd keep
  // showing whatever they last fetched, with no visible sign the new bank's data ever arrived.
  //
  // If the user arrived here via PeoplePage's wizard ("connect a bank now" for a just-created
  // profile), pendingBankOwnerIdRef carries that profile's id — once the connection completes,
  // find the newest still-unassigned connection (i.e. the one that was just created; connecting
  // itself always stays unscoped, see handleConnectBank) and assign it to that profile, same PATCH
  // handleSetConnectionOwner uses. Skipped entirely when there's no pending profile (the normal case).
  useEffect(() => {
    const assignPendingOwnerIfAny = async () => {
      const ownerId = pendingBankOwnerIdRef.current;
      if (!ownerId) return;
      const res = await fetch(`${config.apiUrl}/api/truelayer/connections`, { headers: headers() });
      if (!res.ok) return;
      const data = await res.json();
      const connections: BankConnection[] = data.connections || [];
      const newest = connections
        .filter(c => !c.owner_id)
        .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0];
      if (newest) {
        await fetch(`${config.apiUrl}/api/${newest.provider}/connections/${newest.id}/owner`, {
          method: 'PATCH', headers: headers(), body: JSON.stringify({ owner_id: ownerId }),
        });
      }
      pendingBankOwnerIdRef.current = null;
    };
    const handleConnected = () => {
      assignPendingOwnerIfAny().finally(() => {
        fetchBankData();
        fetchData(true);
        fetchMonthlyTrend(true);
      });
    };
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'truelayer-auth-complete') {
        handleConnected();
      }
    };
    window.addEventListener('message', handleMessage);
    window.addEventListener('focus', handleConnected);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('focus', handleConnected);
    };
  }, [fetchBankData, fetchData, fetchMonthlyTrend, headers]);

  // Invoices/receipts feed the TOTAL SPENT / NET KPI cards and the Income vs Expenses chart's
  // Expenses series too — any change to the list (upload, delete, item include/exclude toggle)
  // needs all three refreshed together, silently.
  const refreshInvoicesAndSummary = useCallback(() => {
    fetchInvoices(true);
    fetchData(true);
    fetchMonthlyTrend(true);
  }, [fetchInvoices, fetchData, fetchMonthlyTrend]);

  // A proof (receipt/invoice) attached to, unlinked from, or deleted off a bank transaction
  // changes both the invoices list (its bank_transaction_id changed) and that transaction's own
  // linked_proofs — refresh everything that could be showing either.
  const refreshAfterProofChange = useCallback(() => {
    refreshInvoicesAndSummary();
    fetchBankData();
    if (expandedStatementId) refetchStatementDetail(expandedStatementId);
  }, [refreshInvoicesAndSummary, fetchBankData, expandedStatementId, refetchStatementDetail]);

  const handleDeleteInvoice = async () => {
    if (!deleteInvoiceTarget) return;
    setDeletingInvoice(true);
    setDeleteInvoiceError(null);
    try {
      const res = await fetch(`${config.apiUrl}/api/invoices/${deleteInvoiceTarget}`, { method: 'DELETE', headers: headers() });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Delete failed (${res.status})`);
      }
      setDeleteInvoiceTarget(null);
      refreshAfterProofChange();
    } catch (error: any) {
      // Previously this fetch had no error handling at all — if it rejected (network error) or
      // came back non-2xx, the code after the await never ran: the dialog just sat there with no
      // feedback, looking like the Delete button did nothing. Now the failure is visible instead.
      setDeleteInvoiceError(error.message || 'Failed to delete invoice');
    } finally {
      setDeletingInvoice(false);
    }
  };

  const handleConnectBank = async (mfaCodeOverride?: string) => {
    setConnecting(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/auth/truelayer/start`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(mfaCodeOverride ? { mfa_code: mfaCodeOverride } : {}),
      });
      const data = await res.json();
      if (res.ok) {
        window.open(data.auth_url, '_blank');
        setMfaDialogOpen(false);
        setMfaCode('');
        setMfaError(null);
        return;
      }
      if (data.consent_required) {
        setConsentDialogOpen(true);
        return;
      }
      if (data.mfa_enrollment_required) {
        setMfaEnrollDialogOpen(true);
        return;
      }
      if (data.mfa_code_required) {
        setMfaError(null);
        setMfaDialogOpen(true);
        return;
      }
      setMfaError(data.error || 'Failed to connect bank');
      setMfaDialogOpen(true);
    } finally {
      setConnecting(false);
    }
  };

  // Set (from the `connectOwnerId` query param below) when the user arrived here straight from
  // PeoplePage's wizard having chosen "connect a bank now" for a profile they just created.
  // Consumed by the postMessage/focus listener effect above once the OAuth tab reports back.
  const pendingBankOwnerIdRef = useRef<string | null>(null);

  // PeoplePage's wizard hands off here (rather than duplicating the whole MFA/consent-aware
  // connect flow itself) by navigating to /accounting?connectOwnerId=<id>. Runs once on mount:
  // stash the id for the listener effect above, kick off the same connect flow the "Connect bank"
  // button uses, then strip the query param so a refresh doesn't repeat it.
  useEffect(() => {
    const ownerIdToConnect = new URLSearchParams(window.location.search).get('connectOwnerId');
    if (!ownerIdToConnect) return;
    pendingBankOwnerIdRef.current = ownerIdToConnect;
    handleConnectBank();
    navigate('/accounting', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAcceptBankConsent = async () => {
    setAcceptingConsent(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/users/consent/bank-data`, { method: 'POST', headers: headers() });
      if (res.ok) {
        setConsentDialogOpen(false);
        handleConnectBank();
      }
    } finally {
      setAcceptingConsent(false);
    }
  };

  // Starts (or restarts) 2FA enrollment inline — same POST /api/mfa/enroll ProfilePage.tsx uses,
  // just rendered in this page's own dialog instead of navigating away.
  const handleStartMfaEnroll = async () => {
    setMfaEnrollError(null);
    setMfaEnrolling(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/mfa/enroll`, { method: 'POST', headers: headers(), body: JSON.stringify({}) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start MFA enrollment');
      setMfaQrCode(data.qr_code);
      setMfaSecret(data.secret);
    } catch (err: any) {
      setMfaEnrollError(err.message || 'Failed to start MFA enrollment');
    } finally {
      setMfaEnrolling(false);
    }
  };

  // Confirms the 6-digit code, then immediately retries the bank connection that triggered this
  // dialog in the first place — the whole point of doing this inline instead of on /profile.
  const handleConfirmMfaEnroll = async () => {
    if (!mfaConfirmCode.trim()) return;
    setMfaEnrollError(null);
    setMfaConfirming(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/mfa/confirm`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ code: mfaConfirmCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid code');
      setMfaEnrollDialogOpen(false);
      setMfaQrCode(null);
      setMfaSecret(null);
      setMfaConfirmCode('');
      handleConnectBank();
    } catch (err: any) {
      setMfaEnrollError(err.message || 'Invalid code');
    } finally {
      setMfaConfirming(false);
    }
  };

  // `full` re-requests the full 365-day lookback from today instead of the usual incremental
  // sync (which only ever looks forward from last_synced_at and can never reach further back
  // than whatever the very first sync originally pulled) — the "Resync full history" action.
  const handleSyncBank = async (connection: BankConnection, full = false) => {
    setSyncing(true);
    setBankSyncError(null);
    try {
      const res = await fetch(`${config.apiUrl}/api/${connection.provider}/connections/${connection.id}/sync`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ full }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setBankSyncError(data.error || `${prettyInstitutionName(connection.institution_name)} couldn't be synced — try again shortly.`);
      }
      // Refetch regardless of outcome — on a needs_reauth 401 the backend already persisted
      // that status, and this is what surfaces it (dot turns red, "Reconnect" appears).
      fetchBankData();
      fetchData(true);
      fetchMonthlyTrend(true);
    } catch {
      setBankSyncError('Network error — please try again.');
    } finally {
      setSyncing(false);
    }
  };

  // One sync button for the whole "Your banks" panel instead of one per connection — syncs
  // every connected bank in parallel, then refreshes exactly like a single-bank sync does.
  // Promise.allSettled (not .all) so one bank failing doesn't stop the others from syncing or
  // throw an unhandled rejection past the try/catch below.
  const handleSyncAllBanks = async () => {
    if (bankConnections.length === 0) return;
    setSyncing(true);
    setBankSyncError(null);
    try {
      const results = await Promise.allSettled(bankConnections.map(async conn => {
        const res = await fetch(`${config.apiUrl}/api/${conn.provider}/connections/${conn.id}/sync`, {
          method: 'POST', headers: headers(), body: JSON.stringify({ full: false }),
        });
        if (!res.ok) throw new Error(prettyInstitutionName(conn.institution_name) || 'A bank');
      }));
      const failedNames = results
        .map((r, i) => (r.status === 'rejected' ? (prettyInstitutionName(bankConnections[i].institution_name) || 'A bank') : null))
        .filter(Boolean);
      if (failedNames.length > 0) {
        setBankSyncError(`Couldn't sync: ${failedNames.join(', ')}. Try again shortly, or reconnect if the dot's turned red.`);
      }
      fetchBankData();
      fetchData(true);
      fetchMonthlyTrend(true);
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnectBank = async () => {
    if (!disconnectTarget) return;
    await fetch(`${config.apiUrl}/api/${disconnectTarget.provider}/connections/${disconnectTarget.id}`, { method: 'DELETE', headers: headers() });
    setDisconnectTarget(null);
    fetchBankData();
  };

  // Disconnect every connected bank in one action — same per-connection DELETE the single
  // "Disconnect" button already uses, just fanned out across all of them at once.
  const handleDisconnectAllBanks = async () => {
    await Promise.all(bankConnections.map(conn =>
      fetch(`${config.apiUrl}/api/${conn.provider}/connections/${conn.id}`, { method: 'DELETE', headers: headers() })
    ));
    setDisconnectAllConfirmOpen(false);
    fetchBankData();
  };

  // Assign/reassign which Owner "book" entity a connection's transactions belong to — connect
  // itself stays unscoped (see handleConnectBank), entity assignment happens here afterward.
  const handleSetConnectionOwner = async (connection: BankConnection, ownerId: string | null) => {
    await fetch(`${config.apiUrl}/api/${connection.provider}/connections/${connection.id}/owner`, {
      method: 'PATCH', headers: headers(), body: JSON.stringify({ owner_id: ownerId }),
    });
    fetchBankData();
  };

  // Shared "Accounts" filter — connectionIds === null means "all connections" (the default);
  // toggling one for the first time expands that into an explicit list before removing/adding,
  // same pattern as BankConnectionDetailDialog's own filter chips. Feeds both the KPI cards and
  // the chart identically (see fetchData/fetchMonthlyTrend above).
  const toggleConnectionFilter = (id: string) => {
    setConnectionIds(prev => {
      const current = prev && prev.length > 0 ? prev : bankConnections.map(c => c.id);
      if (current.includes(id)) {
        if (current.length === 1) return current; // always keep at least one bank selected
        return current.filter(x => x !== id);
      }
      return [...current, id];
    });
  };

  // ── Invoices & receipts (flat lists, no property/flat tree) ─────────────────

  const invoiceList = allInvoiceRecords.filter(r => r.document_type !== 'Receipt');
  const receiptList = allInvoiceRecords.filter(r => r.document_type === 'Receipt');
  // A standalone (unlinked) upload starts confirmed=false — "pending decision" — until the user
  // says whether it's a new expense or proof of an existing transaction (see Invoice.confirmed
  // and ConfirmNewExpenseDialog). Pending items are shown in their own section and excluded from
  // both the per-section totals below and the backend's spend sums, so they can't silently count
  // twice once resolved.
  const isPending = (r: InvoiceRecord) => r.confirmed === false;
  // Most recent first — invoice_date is stored 'YYYY-MM-DD', so a plain string comparison sorts
  // chronologically without needing to parse dates.
  const byDateDesc = (a: InvoiceRecord, b: InvoiceRecord) => (b.invoice_date || '').localeCompare(a.invoice_date || '');
  const pendingInvoiceRecords = allInvoiceRecords.filter(isPending).sort(byDateDesc);
  const invoicesConfirmed = invoiceList.filter(r => !isPending(r)).sort(byDateDesc);
  const receiptsConfirmed = receiptList.filter(r => !isPending(r)).sort(byDateDesc);
  const invoicesTotal = invoicesConfirmed.reduce((s, r) => s + Number(r.effective_total ?? r.total), 0);
  const receiptsTotal = receiptsConfirmed.reduce((s, r) => s + Number(r.effective_total ?? r.total), 0);

  // Default the Receipts/Invoices tab to whichever one actually has confirmed items once the
  // data's loaded — e.g. a user with invoices but zero receipts should land straight on
  // "Invoices" rather than "Receipts" (hardcoded default) showing an empty state while their
  // real invoices sit one click away, unseen, behind the other tab. Only runs once per load
  // (not on every render) and never overrides a tab the user's already clicked themselves.
  const hasAutoSelectedInvoiceTab = useRef(false);
  useEffect(() => {
    if (invoicesLoading || hasAutoSelectedInvoiceTab.current) return;
    hasAutoSelectedInvoiceTab.current = true;
    if (receiptsConfirmed.length === 0 && invoicesConfirmed.length > 0) setInvoiceTab('invoices');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoicesLoading]);

  // ── "View transactions" merge: a confirmed, unlinked receipt/invoice (self-reported, no real
  // bank transaction behind it — see ConfirmNewExpenseDialog's "Add as new transaction" choice)
  // is shown in the same dialog as a pseudo-account, "Manual entry" — reuses every bit
  // of that dialog's existing per-account filter/sort/month-grouping for free instead of a
  // second bespoke view. Only unlinked+confirmed rows qualify; a linked one already appears
  // attached as "proof" under its real transaction instead. excluded_from_transaction_list lets
  // the user remove one specific entry from this merged list entirely (it reverts to a normal
  // Receipts/Invoices entry, see handleToggleCashExcluded) without touching confirmed/linked status.
  const cashConfirmedRecords = allInvoiceRecords.filter(r => r.confirmed !== false && !r.bank_transaction_id && !r.bank_statement_transaction_id && !r.excluded_from_transaction_list);
  const cashTransactionRows = cashConfirmedRecords.map(r => ({
    id: r.id,
    bank_connection_id: 'cash',
    // positive = out, negative = in, matching this dialog's own sign convention — see
    // Invoice.direction (only ever 'money_in' for a manually-added credit entry; every real
    // uploaded receipt/invoice defaults 'money_out').
    amount: r.direction === 'money_in' ? `-${r.effective_total ?? r.total}` : (r.effective_total ?? r.total),
    iso_currency_code: 'GBP',
    date: r.invoice_date,
    name: r.contractor_name,
    merchant_name: r.contractor_name,
    category: r.document_type === 'Receipt' ? 'Receipt' : 'Invoice',
    pending: false,
    linked_proofs: [],
    isCash: true,
    sourceInvoice: r,
  }));
  const mergedBankTransactions = [...bankTransactions, ...cashTransactionRows];
  const mergedBankConnections = cashTransactionRows.length > 0
    ? [...bankConnections, { id: 'cash', institution_name: 'Manual entry', account_holder_name: null, account_number: null, account_mask: null, account_iban: null }]
    : bankConnections;

  // "Add new transaction" → "Yes, from my uploads" candidates: every already-uploaded receipt/
  // invoice that isn't already visible as a transaction — still-pending ones (never yet decided,
  // see pendingInvoiceRecords above) plus confirmed ones the user previously took OUT of this
  // list via its own "Add to transactions" toggle (excluded_from_transaction_list=true). A linked
  // or already-visible cash one is deliberately excluded — it's already represented, adding it
  // again would double it up.
  const previouslyRemovedCashRecords = allInvoiceRecords.filter(r =>
    r.confirmed !== false && !r.bank_transaction_id && !r.bank_statement_transaction_id && r.excluded_from_transaction_list
  );
  const eligibleDocsForNewTransaction: EligibleDocument[] = [...pendingInvoiceRecords, ...previouslyRemovedCashRecords]
    .map(r => ({ id: r.id, contractor_name: r.contractor_name, invoice_date: r.invoice_date, total: r.effective_total ?? r.total, document_type: r.document_type, confirmed: r.confirmed }));

  // "Add all" shortcut, next to each tab's own Upload button (see the Receipts/Invoices tab row
  // below) — reuses the exact same review UI as the end of a bulk upload, scoped to whichever
  // document type's tab it's shown on, for every invoice/receipt not currently showing as a
  // transaction (pending decision, or previously deferred/hidden).
  const receiptsEligibleForBulkAdd = eligibleDocsForNewTransaction.filter(d => d.document_type === 'Receipt');
  const invoicesEligibleForBulkAdd = eligibleDocsForNewTransaction.filter(d => d.document_type !== 'Receipt');
  // "Unlink all" shortcut, next to each tab's own "Add all" button — the reverse: hides every
  // invoice/receipt (scoped to whichever tab it's clicked from) currently showing as its own cash
  // transaction, all at once.
  const receiptsShownAsTransactions = cashConfirmedRecords.filter(r => r.document_type === 'Receipt');
  const invoicesShownAsTransactions = cashConfirmedRecords.filter(r => r.document_type !== 'Receipt');

  const handleUnlinkAllFromTransactions = async (records: InvoiceRecord[]) => {
    await Promise.all(records.map(r =>
      fetch(`${config.apiUrl}/api/invoices/${r.id}/exclude-from-transactions`, {
        method: 'PATCH', headers: headers(), body: JSON.stringify({ excluded: true }),
      })
    ));
    refreshInvoicesAndSummary();
  };

  const handlePickDocumentForNewTransaction = async (doc: EligibleDocument) => {
    if (doc.confirmed === false) {
      await fetch(`${config.apiUrl}/api/invoices/${doc.id}/confirm`, { method: 'POST', headers: headers(), body: JSON.stringify({}) });
    } else {
      await fetch(`${config.apiUrl}/api/invoices/${doc.id}/exclude-from-transactions`, { method: 'PATCH', headers: headers(), body: JSON.stringify({ excluded: false }) });
    }
    fetchInvoices();
  };

  const handleCreateManualTransaction = async (entry: { date: string; description: string; total: string; direction: 'money_out' | 'money_in' }) => {
    const res = await fetch(`${config.apiUrl}/api/invoices/manual-transaction`, {
      method: 'POST', headers: headers(), body: JSON.stringify({
        invoice_date: entry.date,
        contractor_name: entry.description || undefined,
        total: entry.total || undefined,
        direction: entry.direction,
        owner_id: selectedOwnerId || undefined,
      }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to create transaction');
    }
    fetchInvoices();
  };

  // "Add new transaction" → "Upload a new proof now": a receipt/invoice that doesn't exist yet
  // anywhere in the system, captured fresh (camera/photo library/drag-drop) and uploaded with no
  // bank_transaction_id/bank_statement_transaction_id — there's no real bank feed row behind it,
  // this *becomes* the transaction. Reuses the same upload endpoints as the Receipts/Invoices
  // section (OCR/Gemini extraction, dedup, manual-entry fallback all come for free), just without
  // a link target — see UploadProofDialog.tsx for the reconciliation counterpart of this same
  // upload call, which supplies a link target instead.
  const handleUploadNewCashDocument = async (docType: 'receipt' | 'invoice', file: File): Promise<UploadNewCashOutcome> => {
    const formData = new FormData();
    formData.append('file', file);
    if (selectedOwnerId) formData.append('owner_id', selectedOwnerId);
    const endpoint = docType === 'receipt' ? '/api/receipts/upload' : '/api/invoices/upload';
    const res = await fetch(`${config.apiUrl}${endpoint}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    const data = await res.json().catch(() => ({}));

    if (res.ok) {
      // A standalone upload lands confirmed=false (see _create_receipt_invoice) — it wouldn't
      // otherwise show up as a cash transaction until confirmed.
      await fetch(`${config.apiUrl}/api/invoices/${data.invoice.id}/confirm`, { method: 'POST', headers: headers(), body: JSON.stringify({}) });
      fetchInvoices();
      return { status: 'success' };
    }
    if (res.status === 409 && data.invoice) {
      return { status: 'duplicate', invoice: data.invoice };
    }
    if (data.manual_entry_available && data.file_info) {
      return { status: 'manual_entry', fileInfo: { ...data.file_info, owner_id: selectedOwnerId }, error: data.error || null };
    }
    throw new Error(data.error || 'Upload failed');
  };

  // The 409-duplicate resolution for handleUploadNewCashDocument above — unlike
  // handlePickDocumentForNewTransaction's candidate list (already filtered to pending/removed
  // only), the file that collided could be a doc in ANY state, so every state needs handling: not
  // yet confirmed (confirm it), confirmed but previously removed from this list (un-exclude it),
  // already confirmed+visible (nothing to do, it's already there), or already linked as proof of
  // a different real bank transaction (refuse — pulling it in here would silently steal it away).
  const handleUseDuplicateCashDocument = async (doc: EligibleDocument & { excluded_from_transaction_list?: boolean; bank_transaction_id?: string | null; bank_statement_transaction_id?: string | null }) => {
    if (doc.bank_transaction_id || doc.bank_statement_transaction_id) {
      throw new Error('This file is already attached as proof of a different transaction.');
    }
    if (doc.confirmed === false) {
      await fetch(`${config.apiUrl}/api/invoices/${doc.id}/confirm`, { method: 'POST', headers: headers(), body: JSON.stringify({}) });
    } else if (doc.excluded_from_transaction_list) {
      await fetch(`${config.apiUrl}/api/invoices/${doc.id}/exclude-from-transactions`, { method: 'PATCH', headers: headers(), body: JSON.stringify({ excluded: false }) });
    }
    fetchInvoices();
  };

  // ManualReceiptEntryDialog's extraction-failure fallback also lands confirmed=false with no
  // link ids supplied — same confirm step as the happy path above.
  const handleManualEntryCreatedForCash = async (invoice: { id: string }) => {
    await fetch(`${config.apiUrl}/api/invoices/${invoice.id}/confirm`, { method: 'POST', headers: headers(), body: JSON.stringify({}) });
    fetchInvoices();
  };

  // Inline edits (description, amount, direction) on a cash row directly in the transactions
  // table — see BankConnectionDetailDialog's own inline-edit UI for isCash rows.
  const handleEditCashTransaction = async (invoiceId: string, updates: { contractor_name?: string; total?: string; direction?: 'money_out' | 'money_in' }) => {
    await fetch(`${config.apiUrl}/api/invoices/${invoiceId}`, { method: 'PUT', headers: headers(), body: JSON.stringify(updates) });
    fetchInvoices();
    // A direction flip (debit\u2194credit) or amount edit changes the Income/Expenses split, so the
    // chart and KPI summary need refreshing too, not just the invoices list \u2014 same pattern as
    // handleToggleBankTxnExcluded/handleBulkSetBankTxnTag below.
    fetchData(true);
    fetchMonthlyTrend(true);
  };

  // Removes a self-reported "cash" entry from the merged transaction list entirely — it goes
  // back to being a normal Receipts/Invoices entry (still fully counted toward spend), just no
  // longer shown here. Reversible from the Receipts/Invoices card itself ("Add to transactions").
  const handleToggleCashExcluded = async (invoiceId: string, excluded: boolean) => {
    await fetch(`${config.apiUrl}/api/invoices/${invoiceId}/exclude-from-transactions`, {
      method: 'PATCH', headers: headers(), body: JSON.stringify({ excluded }),
    });
    fetchInvoices();
  };

  // The × on a cash transaction row (BankConnectionDetailDialog's onRemoveCash) — a document-less
  // entry (created via "Add without a proof"/manual-transaction, pdf_filename/original_filename
  // both NULL) has nothing worth keeping around as a Receipts/Invoices card, so removing it here
  // deletes it outright instead of just excluding it. A real uploaded receipt/invoice shown as
  // cash still only gets excluded (see handleToggleCashExcluded above) — deleting it would destroy
  // an actual uploaded document, not just tidy up a placeholder.
  const handleRemoveCashTransaction = async (invoice: { id: string; pdf_filename?: string | null; original_filename?: string | null }) => {
    if (!invoice.pdf_filename && !invoice.original_filename) {
      await fetch(`${config.apiUrl}/api/invoices/${invoice.id}`, { method: 'DELETE', headers: headers() });
      refreshAfterProofChange();
    } else {
      await handleToggleCashExcluded(invoice.id, true);
    }
  };

  // Marks a real bank-feed transaction as "not relevant to my business account" — persisted,
  // reversible (see BankTransaction.excluded), excludes it from every Income/Expenses sum. The
  // row itself is never deleted, just shown muted with a toggle to bring it back.
  const handleToggleBankTxnExcluded = async (transactionId: string, excluded: boolean) => {
    await fetch(`${config.apiUrl}/api/truelayer/transactions/${transactionId}/exclude`, {
      method: 'PATCH', headers: headers(), body: JSON.stringify({ excluded }),
    });
    fetchBankData();
    fetchData(true);
    fetchMonthlyTrend(true);
  };

  // Applies the same hide/show state to every other transaction sharing the same tag in one
  // call — the "hide/show the other N transactions tagged 'X' too?" bulk prompt.
  const handleBulkToggleBankTxnExcluded = async (transactionIds: string[], excluded: boolean) => {
    await fetch(`${config.apiUrl}/api/truelayer/transactions/bulk-exclude`, {
      method: 'PATCH', headers: headers(), body: JSON.stringify({ transaction_ids: transactionIds, excluded }),
    });
    fetchBankData();
    fetchData(true);
    fetchMonthlyTrend(true);
  };

  // Sets/clears a live-feed transaction's free-text tag (see BankTransaction.tag) — e.g. tagging
  // it "Rent" for future chart/reporting use. Purely a label, doesn't affect any KPI/chart sum.
  const handleSetBankTxnTag = async (transactionId: string, tag: string) => {
    await fetch(`${config.apiUrl}/api/truelayer/transactions/${transactionId}/tag`, {
      method: 'PATCH', headers: headers(), body: JSON.stringify({ tag }),
    });
    fetchBankData();
  };

  // Applies the same tag to every other transaction sharing an identical description in one
  // call — the "tag the other N transactions with this description too?" bulk-tag prompt.
  const handleBulkSetBankTxnTag = async (transactionIds: string[], tag: string) => {
    await fetch(`${config.apiUrl}/api/truelayer/transactions/bulk-tag`, {
      method: 'PATCH', headers: headers(), body: JSON.stringify({ transaction_ids: transactionIds, tag }),
    });
    fetchBankData();
  };

  // Same as above but for an uploaded-statement transaction (see BankStatementTransaction.excluded).
  const handleToggleStatementTxnExcluded = async (transactionId: string, excluded: boolean) => {
    await fetch(`${config.apiUrl}/api/bank-statement-transactions/${transactionId}`, {
      method: 'PATCH', headers: headers(), body: JSON.stringify({ excluded }),
    });
    fetchBankStatements();
    fetchData(true);
    fetchMonthlyTrend(true);
  };


  // ── KPI cards ───────────────────────────────────────────────────────────────
  // All cards in both modes are hand-written JSX below (not a generic array) since Collected/
  // Expenses now carry an Invoices/Receipts breakdown, Collected also folds in Outstanding +
  // Deposits, and Net needs the shared math tooltip + calculator — see netCardMath() below.

  // Shared by both modes' Net card — Property: Collected - Total spent; General: Income - Expenses.
  const netCardMath = (incomeLabel: string, incomeValue: string, spentValue: string) => {
    const incomeNum = Number(incomeValue);
    const spentNum = Number(spentValue);
    const netNum = incomeNum - spentNum;
    const positive = netNum >= 0;
    return {
      netNum,
      positive,
      display: `${positive ? '+' : ''}${fmtGBP(netNum)}`,
      tooltip: `${incomeLabel} ${fmtGBP(incomeNum)} − Spent ${fmtGBP(spentNum)} = Net ${positive ? '' : '−'}${fmtGBP(Math.abs(netNum))}`,
    };
  };

  // True whenever the active period is one of the "More ranges" section's own options rather
  // than one of the 3 always-visible rows (This month/This quarter/This year) — used to
  // auto-expand that section when the period dropdown is opened, so the current selection is
  // never hidden a click deeper than necessary.
  const moreRangesActive = period === 'last_month' || period === 'last_quarter' || period === 'last_year' || period === 'all' || period === 'custom';

  // Single label describing the shared "Accounts" scope — feeds both the toolbar button and the
  // one combined subtitle line under the page title (replacing the period caption that used to
  // repeat under every KPI card and again on the chart).
  const accountsLabel = (() => {
    const base = bankSource === 'statements'
      ? 'Bank statements'
      : bankConnections.length === 0
        ? 'No bank connected'
        : (!connectionIds || connectionIds.length === 0 || connectionIds.length === bankConnections.length)
          ? 'All accounts'
          : bankConnections.filter(c => connectionIds.includes(c.id)).map(c => prettyInstitutionName(c.institution_name)).join(', ');
    if (!includeManualEntries) return base;
    // No bank connected but manual entries are included — "No bank connected + Manual entry" is
    // redundant noise here since there's nothing else the label could possibly mean.
    if (bankSource === 'connections' && bankConnections.length === 0) return 'Manual entry';
    return `${base} + Manual entry`;
  })();

  // ── render ──────────────────────────────────────────────────────────────────

  const hour = new Date().getHours();
  const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  const firstName = (user?.name?.split(' ')[0] || '').replace(/^./, c => c.toUpperCase());
  const heroBg = palette.tokens.primaryActive;
  const heroOnColor = palette.tokens.onPrimary;
  const heroSubtleColor = alpha(palette.tokens.onPrimary, 0.78);
  const heroAccentColor = palette.tokens.accentAmber;
  const netForHero = netCardMath('Income', summary.bank_income, summary.expenses_general);

  // One combined "last synced" line for the whole "Your banks" panel instead of one per
  // connection — the most recent sync across every connected bank.
  const lastBankSyncedAt = bankConnections.reduce<string | null>((latest, c) => {
    if (!c.last_synced_at) return latest;
    if (!latest || new Date(c.last_synced_at) > new Date(latest)) return c.last_synced_at;
    return latest;
  }, null);

  // "View transactions" (moved next to the toolbar filters) opens pre-scoped to whatever the
  // page's own Accounts/period filters are currently set to — same accounts, same period — while
  // still letting the user broaden either one from inside the dialog without touching these. The
  // page-level Accounts filter only ever offers real TrueLayer connections as choices (it has no
  // concept of "Manual entry" at all), so a narrowed selection must never silently hide
  // the cash pseudo-connection too — otherwise a receipt/invoice just added via "Add new
  // transaction" → "From uploads" would save correctly but never appear in a dialog that opened
  // scoped to one specific bank account.
  const bankTxnDialogInitialConnectionIds = (bankSource === 'connections' && connectionIds && connectionIds.length > 0)
    ? (cashTransactionRows.length > 0 ? [...connectionIds, 'cash'] : connectionIds)
    : null;
  const bankTxnDialogInitialMonthKeys = monthlyTrend.map(m => monthLabelToKey(m.label)).filter((k): k is string => k !== null);
  // Deliberately NOT gated on bankConnections.length === 0 — a user with no bank connected at all
  // but manually-added invoices/receipts (cashTransactionRows) still has transactions worth viewing.
  const noTransactionsYet = bankTransactions.length === 0 && cashTransactionRows.length === 0;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1440, mx: 'auto' }}>

      {/* ── welcome hero — same shape as PropertiesPage.tsx's own hero (coral banner, date +
          status pill, big serif greeting, cream stats footer) — and, to its right, a "Your
          banks" sidebar card in the same slot PropertiesPage.tsx gives its "Profiles" panel. */}
      <Box sx={{ display: { xs: 'block', md: 'flex' }, gap: 2, mb: 5, alignItems: 'stretch' }}>
        <Box sx={{
          flex: 3,
          borderRadius: '14px', overflow: 'hidden', mb: { xs: 2, md: 0 },
          border: `1px solid ${palette.tokens.hairline}`, boxShadow: `0 1px 3px ${alpha(palette.tokens.ink, 0.06)}`,
        }}>
          <Box sx={{ bgcolor: heroBg, pt: { xs: 2.7, md: 3.6 }, px: { xs: 2.7, md: 3.6 }, pb: { xs: 1.8, md: 2.2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.8, flexWrap: 'wrap' }}>
              <Box sx={{ bgcolor: alpha(heroOnColor, 0.18), borderRadius: 10, px: 1.35, py: 0.36 }}>
                <Typography sx={{ fontSize: '0.65rem', color: heroOnColor, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                  {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                </Typography>
              </Box>
              {bankConnections.length === 0 && (
                <Box sx={{ bgcolor: alpha(heroOnColor, 0.18), border: `1px solid ${alpha(heroOnColor, 0.3)}`, borderRadius: 10, px: 1.35, py: 0.36 }}>
                  <Typography sx={{ fontSize: '0.65rem', color: heroOnColor, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                    Let's get started
                  </Typography>
                </Box>
              )}
            </Box>

            <Typography variant="displaySm" component="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.52rem' }, fontWeight: 400, color: heroOnColor, lineHeight: 1.2, mb: 1.35, fontVariantNumeric: 'lining-nums' }}>
              Good {timeOfDay}{firstName ? `, ${firstName}` : ''}.{' '}
              {bankConnections.length === 0 ? (
                <>Let's connect your first <Box component="span" sx={{ color: heroAccentColor }}>bank.</Box></>
              ) : (
                <>You've connected{' '}
                  <Box component="span" sx={{ color: heroAccentColor }}>
                    {bankConnections.length} {bankConnections.length === 1 ? 'bank' : 'banks'}.
                  </Box>
                </>
              )}
            </Typography>

            {bankConnections.length === 0 ? (
              <Typography sx={{ color: heroSubtleColor, fontSize: '0.81rem', lineHeight: 1.6 }}>
                Connect a bank account to start tracking income, expenses, and cash flow automatically.
              </Typography>
            ) : (
              <Typography sx={{ color: heroSubtleColor, fontSize: { xs: '0.95rem', md: '1.05rem' }, fontWeight: 500, lineHeight: 1.6 }}>
                Use the filters below to see your cash flow.
              </Typography>
            )}
          </Box>

          {/* Filters — moved down into the hero's own white space (rather than sitting as a
              separate toolbar further down the page) so they read as part of this same card:
              the greeting/caption above says what's connected and invites filtering, this row
              lets you narrow it down. Each control gets an even share of the card's full width
              (flex: 1) rather than clustering to the left. Given extra vertical breathing room
              (reclaimed from the coral banner above, whose own bottom padding was tightened) so
              the controls read as bigger/more prominent rather than a cramped afterthought.
              Shown even with zero banks connected — every control here (Year/Range/View, and
              Source's own "No bank connected"/"Manual entry" states) already degrades gracefully
              with no connections, and keeping this section always present is what gives the hero
              card its normal fixed height instead of collapsing to just the coral banner (which
              would otherwise force the "Your banks" sidebar's own empty state to dictate a much
              taller stretched row than any other greeting hero on the site uses). */}
          <Box sx={{ bgcolor: palette.tokens.canvas, pt: { xs: 3.2, md: 4.2 }, pb: { xs: 3.2, md: 4.2 }, px: { xs: 2.7, md: 3.6 } }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: { xs: 2.25, sm: 2.7 } }}>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ borderTop: `1px solid ${palette.tokens.hairlineSoft}`, pt: '13px', mb: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => setAccountsMenuAnchorEl(e.currentTarget)}
                    startIcon={<BankIcon sx={{ fontSize: 17 }} />}
                    endIcon={accountsMenuAnchorEl ? <CollapseIcon sx={{ fontSize: 20 }} /> : <ExpandIcon sx={{ fontSize: 20 }} />}
                    sx={{ borderRadius: 2, fontWeight: 600, fontSize: 14, py: 0.75, borderColor: palette.tokens.hairline, color: palette.tokens.ink, bgcolor: palette.tokens.surfaceCreamStrong, maxWidth: 170, minWidth: 0 }}
                  >
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                      {accountsLabel}
                    </Box>
                  </Button>
                  </Box>
                  <Typography sx={{ fontSize: '0.82rem', color: palette.tokens.muted, textAlign: 'center', mt: 0.6 }}>Source</Typography>

                  <Popover
                    open={Boolean(accountsMenuAnchorEl)}
                    anchorEl={accountsMenuAnchorEl}
                    onClose={() => setAccountsMenuAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  >
                    <Box sx={{ p: 2, width: 260 }}>
                      <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                        Source
                      </Typography>
                      {/* The live TrueLayer feed and uploaded statements are treated as mutually
                          exclusive sources (never blended, see backend's bank_source docs). */}
                      <Box sx={{ display: 'flex', mb: 1.5, border: `1px solid ${palette.tokens.hairline}`, borderRadius: 2, p: 0.25 }}>
                        {(['connections', 'statements'] as const).map(s => (
                          <Button
                            key={s}
                            size="small"
                            fullWidth
                            variant={bankSource === s ? 'contained' : 'text'}
                            onClick={() => setBankSource(s)}
                            sx={{ borderRadius: 1.5, fontWeight: 600, fontSize: 12, ...(bankSource === s ? {} : { color: palette.tokens.muted }) }}
                          >
                            {s === 'connections' ? 'Bank connections' : 'Bank statements'}
                          </Button>
                        ))}
                      </Box>

                      {bankSource === 'connections' && (
                        bankConnections.length === 0 ? (
                          <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft }}>No bank connected yet.</Typography>
                        ) : (
                          <>
                            <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                              Accounts
                            </Typography>
                            {bankConnections.map(conn => {
                              const isSelected = connectionIds ? connectionIds.includes(conn.id) : true;
                              const owner = conn.owner_id ? owners.find(o => o.id === conn.owner_id) : null;
                              return (
                                <Box
                                  key={conn.id}
                                  onClick={() => toggleConnectionFilter(conn.id)}
                                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25, borderRadius: 1.5, cursor: 'pointer', '&:hover': { bgcolor: palette.tokens.canvas } }}
                                >
                                  <Checkbox size="small" checked={isSelected} sx={{ p: 0.75 }} />
                                  <Typography variant="body2" noWrap sx={{ fontSize: 13, minWidth: 0, flex: 1 }}>
                                    {prettyInstitutionName(conn.institution_name)}
                                    {conn.account_mask && <Box component="span" sx={{ color: palette.tokens.mutedSoft }}> ····{conn.account_mask}</Box>}
                                    {owner && <Box component="span" sx={{ color: palette.tokens.mutedSoft }}> @{owner.name}</Box>}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </>
                        )
                      )}
                      {bankSource === 'statements' && bankStatements.length === 0 && (
                        <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft }}>No statements uploaded yet.</Typography>
                      )}

                      {/* Manual entry isn't a real bank connection or statement, so it's not part
                          of the exclusive toggle above — it's an additive checkbox that layers
                          on top of whichever of those is selected (see backend's include_manual
                          param). */}
                      <Divider sx={{ my: 1.5 }} />
                      <Box
                        onClick={() => setIncludeManualEntries(v => !v)}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25, borderRadius: 1.5, cursor: 'pointer', '&:hover': { bgcolor: palette.tokens.canvas } }}
                      >
                        <Checkbox size="small" checked={includeManualEntries} sx={{ p: 0.75 }} />
                        <Typography variant="body2" sx={{ fontSize: 13 }}>Manual entry</Typography>
                      </Box>
                    </Box>
                  </Popover>
                </Box>

                {/* Calendar vs fiscal year — its own small dropdown, preselected to whichever
                    is active; applies wherever "year" is relevant (This year/Last year/quarter
                    labels) regardless of which period below is currently selected. */}
                <Box>
                  <Box sx={{ borderTop: `1px solid ${palette.tokens.hairlineSoft}`, pt: '13px', mb: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => setYearTypeMenuAnchorEl(e.currentTarget)}
                    endIcon={yearTypeMenuAnchorEl ? <CollapseIcon sx={{ fontSize: 20 }} /> : <ExpandIcon sx={{ fontSize: 20 }} />}
                    sx={{ borderRadius: 2, fontWeight: 600, fontSize: 14, py: 0.75, borderColor: palette.tokens.hairline, color: palette.tokens.ink, bgcolor: palette.tokens.surfaceCreamStrong }}
                  >
                    {yearType === 'fiscal' ? 'Fiscal year' : 'Calendar year'}
                  </Button>
                  </Box>
                  <Typography sx={{ fontSize: '0.82rem', color: palette.tokens.muted, textAlign: 'center', mt: 0.6 }}>Year</Typography>
                  <Popover
                    open={Boolean(yearTypeMenuAnchorEl)}
                    anchorEl={yearTypeMenuAnchorEl}
                    onClose={() => setYearTypeMenuAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  >
                    <Box sx={{ p: 1, width: 160 }}>
                      {(['calendar', 'fiscal'] as const).map(t => (
                        <Box
                          key={t}
                          onClick={() => { setYearType(t); setYearTypeMenuAnchorEl(null); }}
                          sx={{
                            py: 1, px: 1, borderRadius: 1.5, cursor: 'pointer',
                            bgcolor: yearType === t ? '#eff6ff' : 'transparent',
                            color: yearType === t ? palette.tokens.primary : palette.tokens.ink,
                            fontWeight: yearType === t ? 700 : 500, fontSize: 14,
                            '&:hover': { bgcolor: yearType === t ? '#eff6ff' : palette.tokens.canvas },
                          }}
                        >
                          {t === 'calendar' ? 'Calendar year' : 'Fiscal year'}
                        </Box>
                      ))}
                    </Box>
                  </Popover>
                </Box>

                {/* One period dropdown, next to Accounts and Calendar/Fiscal year rather than
                    off on its own — preselected to whichever is active (e.g. "This year").
                    This quarter/This month/This year are always visible; "More ranges" expands
                    in place for Last month/Last quarter/Last year/All time/Custom range,
                    instead of pill buttons plus a separate "More ranges" trigger. */}
                <Box>
                  <Box sx={{ borderTop: `1px solid ${palette.tokens.hairlineSoft}`, pt: '13px', mb: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      setPeriodMenuAnchorEl(e.currentTarget);
                      setMoreRangesExpanded(moreRangesActive);
                      if (period === 'custom') {
                        setShowCustomInputs(true);
                        setCustomStartDate(appliedCustomStart);
                        setCustomEndDate(appliedCustomEnd);
                      }
                    }}
                    startIcon={<CalendarIcon sx={{ fontSize: 18 }} />}
                    endIcon={periodMenuAnchorEl ? <CollapseIcon sx={{ fontSize: 20 }} /> : <ExpandIcon sx={{ fontSize: 20 }} />}
                    sx={{ borderRadius: 2, px: 1.5, fontWeight: 600, fontSize: 14, py: 0.75, borderColor: palette.tokens.hairline, color: palette.tokens.ink, bgcolor: palette.tokens.surfaceCreamStrong }}
                  >
                    {PERIOD_OPTION_LABELS[period]}
                  </Button>
                  </Box>
                  <Typography sx={{ fontSize: '0.82rem', color: palette.tokens.muted, textAlign: 'center', mt: 0.6 }}>Range</Typography>

                  <Popover
                    open={Boolean(periodMenuAnchorEl)}
                    anchorEl={periodMenuAnchorEl}
                    onClose={() => { setPeriodMenuAnchorEl(null); setShowCustomInputs(false); }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  >
                    <Box sx={{ p: 1, width: 220 }}>
                      {(['this_quarter', 'month', 'this_year'] as const).map(p => (
                        <Box
                          key={p}
                          onClick={() => { setPeriod(p); setPeriodMenuAnchorEl(null); setShowCustomInputs(false); }}
                          sx={{
                            py: 1, px: 1, borderRadius: 1.5, cursor: 'pointer',
                            bgcolor: period === p ? '#eff6ff' : 'transparent',
                            color: period === p ? palette.tokens.primary : palette.tokens.ink,
                            fontWeight: period === p ? 700 : 500, fontSize: 14,
                            '&:hover': { bgcolor: period === p ? '#eff6ff' : palette.tokens.canvas },
                          }}
                        >
                          {PERIOD_OPTION_LABELS[p]}
                        </Box>
                      ))}

                      <Divider sx={{ my: 0.5 }} />

                      <Box
                        onClick={() => setMoreRangesExpanded(v => !v)}
                        sx={{
                          py: 1, px: 1, borderRadius: 1.5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          color: palette.tokens.ink, fontWeight: 500, fontSize: 14,
                          '&:hover': { bgcolor: palette.tokens.canvas },
                        }}
                      >
                        More ranges
                        {moreRangesExpanded ? <CollapseIcon sx={{ fontSize: 18, color: palette.tokens.muted }} /> : <ExpandIcon sx={{ fontSize: 18, color: palette.tokens.muted }} />}
                      </Box>

                      {moreRangesExpanded && (
                        <Box sx={{ pl: 1 }}>
                          {(['last_month', 'last_quarter', 'last_year', 'all'] as const).map(p => (
                            <Box
                              key={p}
                              onClick={() => { setPeriod(p); setPeriodMenuAnchorEl(null); setShowCustomInputs(false); }}
                              sx={{
                                py: 1, px: 1, borderRadius: 1.5, cursor: 'pointer',
                                bgcolor: period === p ? '#eff6ff' : 'transparent',
                                color: period === p ? palette.tokens.primary : palette.tokens.ink,
                                fontWeight: period === p ? 700 : 500, fontSize: 14,
                                '&:hover': { bgcolor: period === p ? '#eff6ff' : palette.tokens.canvas },
                              }}
                            >
                              {PERIOD_OPTION_LABELS[p]}
                            </Box>
                          ))}

                          <Divider sx={{ my: 0.5 }} />

                          <Box
                            onClick={() => setShowCustomInputs(v => !v)}
                            sx={{
                              py: 1, px: 1, borderRadius: 1.5, cursor: 'pointer',
                              bgcolor: period === 'custom' ? '#eff6ff' : 'transparent',
                              color: period === 'custom' ? palette.tokens.primary : palette.tokens.ink,
                              fontWeight: period === 'custom' ? 700 : 500, fontSize: 14,
                              '&:hover': { bgcolor: period === 'custom' ? '#eff6ff' : palette.tokens.canvas },
                            }}
                          >
                            Custom range…
                          </Box>

                          {showCustomInputs && (
                            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <TextField
                                type="date" size="small" label="Start" InputLabelProps={{ shrink: true }}
                                value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)}
                              />
                              <TextField
                                type="date" size="small" label="End" InputLabelProps={{ shrink: true }}
                                value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)}
                              />
                              <Button
                                size="small"
                                variant="contained"
                                disabled={!customStartDate || !customEndDate}
                                onClick={() => {
                                  setAppliedCustomStart(customStartDate);
                                  setAppliedCustomEnd(customEndDate);
                                  setPeriod('custom');
                                  setPeriodMenuAnchorEl(null);
                                }}
                              >
                                Apply
                              </Button>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Popover>
                </Box>

                {/* View — Quarterly/Monthly/Weekly/Daily, same Button+Popover pattern as
                    Source/Year/Range rather than a separate control elsewhere on the page.
                    Options depend on the selected Range (see GRANULARITY_OPTIONS_BY_PERIOD). */}
                <Box>
                  <Box sx={{ borderTop: `1px solid ${palette.tokens.hairlineSoft}`, pt: '13px', mb: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => setGranularityMenuAnchorEl(e.currentTarget)}
                    endIcon={granularityMenuAnchorEl ? <CollapseIcon sx={{ fontSize: 20 }} /> : <ExpandIcon sx={{ fontSize: 20 }} />}
                    sx={{ borderRadius: 2, fontWeight: 600, fontSize: 14, py: 0.75, borderColor: palette.tokens.hairline, color: palette.tokens.ink, bgcolor: palette.tokens.surfaceCreamStrong }}
                  >
                    {granularityOptionsFor(period, appliedCustomStart, appliedCustomEnd).options.find(o => o.key === chartGranularity)?.label || 'View'}
                  </Button>
                  </Box>
                  <Typography sx={{ fontSize: '0.82rem', color: palette.tokens.muted, textAlign: 'center', mt: 0.6 }}>View</Typography>
                  <Popover
                    open={Boolean(granularityMenuAnchorEl)}
                    anchorEl={granularityMenuAnchorEl}
                    onClose={() => setGranularityMenuAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  >
                    <Box sx={{ p: 1, width: 160 }}>
                      {granularityOptionsFor(period, appliedCustomStart, appliedCustomEnd).options.map(({ key, label }) => (
                        <Box
                          key={key}
                          onClick={() => { setChartGranularity(key); setGranularityMenuAnchorEl(null); }}
                          sx={{
                            py: 1, px: 1, borderRadius: 1.5, cursor: 'pointer',
                            bgcolor: chartGranularity === key ? '#eff6ff' : 'transparent',
                            color: chartGranularity === key ? palette.tokens.primary : palette.tokens.ink,
                            fontWeight: chartGranularity === key ? 700 : 500, fontSize: 14,
                            '&:hover': { bgcolor: chartGranularity === key ? '#eff6ff' : palette.tokens.canvas },
                          }}
                        >
                          {label}
                        </Box>
                      ))}
                    </Box>
                  </Popover>
                </Box>
              </Box>
            </Box>
        </Box>

        {/* Your banks — sidebar slot, same visual language as PropertiesPage.tsx's "Profiles"
            panel (dashed "add" affordance, one row per connection/statement). Source-aware via
            the shared bankSource toggle (see the Accounts filter Popover below) — connections and
            statements are mutually exclusive views, never blended, so this card either lists live
            connections or uploaded statements, never both. Each row is just a glance — syncing/
            per-bank management (owner assignment, resync, disconnect) for connections, or view/
            delete for statements, live one level up/out via a "Manage"/"See N more" link into the
            full detail dialog (same cap pattern as PropertiesPage.tsx's owners sidebar). */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{
            height: '100%', borderRadius: '14px', border: `1px solid ${palette.tokens.hairline}`,
            bgcolor: palette.tokens.surfaceCreamStrong, p: 2, display: 'flex', flexDirection: 'column',
          }}>
            {/* Tab row — same pseudo-tab pattern as PropertiesPage.tsx's flat tabs/the
                certificates dashboard: click a tab to switch which section this one card shows.
                Deliberately local (bankPanelSource, not the shared bankSource below) — this is
                just a display switch for the sidebar card, not a data filter, so it never
                touches the KPI cards/chart (those stay scoped to whatever the "Accounts" filter
                Popover's own Source toggle picked). Split evenly (flex: 1 each). */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {(['connections', 'statements'] as const).map(s => {
                const isSelected = bankPanelSource === s;
                return (
                  <Box
                    key={s}
                    onClick={() => setBankPanelSource(s)}
                    sx={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, px: 1.5, py: 0.75,
                      borderRadius: '10px 10px 0 0',
                      bgcolor: isSelected ? palette.tokens.white : 'transparent',
                      border: isSelected ? `1px solid ${palette.tokens.hairline}` : '1px solid transparent',
                      borderBottom: isSelected ? `1px solid ${palette.tokens.white}` : 'none',
                      mb: isSelected ? '-1px' : 0,
                      cursor: 'pointer',
                      position: 'relative',
                      zIndex: isSelected ? 1 : 0,
                      transition: 'background-color 0.15s ease',
                      '&:hover': !isSelected ? { bgcolor: alpha(palette.tokens.white, 0.5) } : {},
                    }}
                  >
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: isSelected ? 700 : 500, color: isSelected ? palette.tokens.ink : palette.tokens.muted, whiteSpace: 'nowrap' }}>
                      {s === 'connections' ? 'Your banks' : 'Your statements'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Content panel for the selected tab — same "cut corner" pairing with the tab row
                above as PropertiesPage.tsx's flat detail panel. `flex: 1` so it fills whatever
                vertical space remains in this fixed-height card (rather than sitting at a small
                natural height with dead space below it), and the empty-state Paper below stretches
                to fill that same space too. */}
            <Box sx={{ bgcolor: palette.tokens.white, border: `1px solid ${palette.tokens.hairline}`, borderRadius: '0 8px 8px 8px', p: 1.5, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 0.25 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: palette.tokens.ink, flex: 1 }}>
                {bankPanelSource === 'statements'
                  ? (bankStatements.length > 0 ? `${bankStatements.length} statement${bankStatements.length === 1 ? '' : 's'}` : '')
                  : (bankConnections.length > 0 ? `${bankConnections.length} connected bank${bankConnections.length === 1 ? '' : 's'}` : '')}
              </Typography>
              {bankPanelSource === 'connections' && bankConnections.length > 0 && (
                <Tooltip title="Sync all banks">
                  <IconButton size="small" disabled={syncing} onClick={handleSyncAllBanks} sx={{ color: palette.tokens.muted }}>
                    {syncing ? <CircularProgress size={14} /> : <SyncIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={
                bankPanelSource === 'statements'
                  ? 'Upload a statement'
                  : connecting ? 'Connecting…' : bankConnections.length === 0 ? 'Connect your bank' : 'Add another bank'
              }>
                <span>
                  <IconButton
                    size="small"
                    disabled={bankPanelSource === 'connections' && connecting}
                    onClick={() => bankPanelSource === 'statements' ? setBankStatementUploadOpen(true) : handleConnectBank()}
                    sx={{ color: palette.tokens.muted }}
                  >
                    <AddBankIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </span>
              </Tooltip>
              {bankPanelSource === 'connections' && bankConnections.length > 0 && (
                <Tooltip title="Disconnect all banks">
                  <IconButton size="small" onClick={() => setDisconnectAllConfirmOpen(true)} sx={{ color: palette.tokens.muted, '&:hover': { color: palette.tokens.error } }}>
                    <DisconnectIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={
                bankPanelSource === 'statements'
                  ? "Already have a PDF bank statement? Upload it and we'll digitize it into the same reconciled view — no live connection needed."
                  : "Banks you've connected for live transactions. We support all types of banks — Revolut, Starling, Monzo, high-street banks and more. Once connected, you'll see your transactions live and can reconcile them automatically."
              }>
                <InfoIcon sx={{ fontSize: 16, color: palette.tokens.muted, cursor: 'help' }} />
              </Tooltip>
            </Box>
            {bankPanelSource === 'connections' ? (
              bankConnections.length > 0 ? (
                <Typography sx={{ color: palette.tokens.mutedSoft, fontSize: '0.7rem', mb: 1.5 }}>
                  {lastBankSyncedAt ? `Synced ${fmtSyncTime(lastBankSyncedAt)}` : 'Not yet synced'}
                </Typography>
              ) : (
                <Box sx={{ mb: 1.5 }} />
              )
            ) : (
              bankStatements.length > 0 ? (
                <Typography sx={{ color: palette.tokens.mutedSoft, fontSize: '0.7rem', mb: 1.5 }}>
                  {bankStatements.length} statement{bankStatements.length === 1 ? '' : 's'} uploaded
                </Typography>
              ) : (
                <Box sx={{ mb: 1.5 }} />
              )
            )}
            {bankPanelSource === 'connections' && bankSyncError && (
              <Alert severity="warning" sx={{ mb: 1.5, py: 0, fontSize: 12 }} onClose={() => setBankSyncError(null)}>
                {bankSyncError}
              </Alert>
            )}

            {/* Fixed at BANK_PANEL_CAP visible rows, so the panel — and the welcome-hero row it
                sits in, since they're flex siblings stretched to match height — never grows
                taller as more banks/statements are added; the full list (with per-row actions)
                lives in the "Manage banks"/"Uploaded statements" dialog instead. */}
            {bankPanelSource === 'connections' ? (() => {
              if (bankConnections.length === 0) {
                return (
                  <Paper elevation={0} sx={{
                    border: `2px dashed ${palette.tokens.hairline}`, borderRadius: 3, p: 2,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                    flex: 1,
                  }}>
                    <BankIcon sx={{ fontSize: 26, color: palette.tokens.hairline }} />
                    <Typography sx={{ fontWeight: 700, color: palette.tokens.ink, fontSize: 13.5, textAlign: 'center' }}>No banks connected yet</Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={connecting ? <CircularProgress size={14} color="inherit" /> : <AddBankIcon sx={{ fontSize: 17 }} />}
                      disabled={connecting}
                      onClick={() => handleConnectBank()}
                      sx={{ borderRadius: 2, fontWeight: 600 }}
                    >
                      {connecting ? 'Connecting…' : 'Connect a bank'}
                    </Button>
                  </Paper>
                );
              }
              const overflowCount = bankConnections.length - BANK_PANEL_CAP;
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {bankConnections.slice(0, BANK_PANEL_CAP).map(conn => {
                    const needsReauth = conn.status === 'needs_reauth';
                    const owner = conn.owner_id ? owners.find(o => o.id === conn.owner_id) : null;
                    return (
                    <Box key={conn.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: `1px solid ${palette.tokens.hairline}`, borderRadius: 2, p: 1.25 }}>
                      <Tooltip title={needsReauth ? 'This connection has expired — reconnect to keep syncing' : 'Connected'}>
                        <Box sx={{ position: 'relative', width: 8, height: 8, flexShrink: 0 }}>
                          {!needsReauth && (
                            <Box sx={{
                              position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: palette.tokens.success,
                              animation: 'pulse-dot 2s ease-out infinite',
                              '@keyframes pulse-dot': {
                                '0%': { transform: 'scale(1)', opacity: 0.35 },
                                '70%': { transform: 'scale(2.2)', opacity: 0 },
                                '100%': { transform: 'scale(2.2)', opacity: 0 },
                              },
                            }} />
                          )}
                          <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: needsReauth ? palette.tokens.error : palette.tokens.success }} />
                        </Box>
                      </Tooltip>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: palette.tokens.ink, fontSize: 13, minWidth: 0, flex: 1 }} noWrap>
                        {prettyInstitutionName(conn.institution_name)}
                        {conn.account_mask && <Box component="span" sx={{ color: palette.tokens.mutedSoft, fontWeight: 500 }}> ····{conn.account_mask}</Box>}
                        {owner && <Box component="span" sx={{ color: palette.tokens.mutedSoft, fontWeight: 500 }}> @{owner.name}</Box>}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        {needsReauth && (
                          <Tooltip title="Reconnect this bank">
                            <IconButton size="small" disabled={connecting} onClick={() => handleConnectBank()} sx={{ color: palette.tokens.error }}>
                              <WarningIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Resync full 365-day history — use this if older transactions seem to be missing">
                          <IconButton size="small" disabled={syncing} onClick={() => handleSyncBank(conn, true)} sx={{ color: palette.tokens.muted }}>
                            {syncing ? <CircularProgress size={14} /> : <ResyncIcon sx={{ fontSize: 15 }} />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Disconnect">
                          <IconButton size="small" onClick={() => setDisconnectTarget(conn)} sx={{ color: palette.tokens.muted, '&:hover': { color: palette.tokens.error } }}>
                            <DisconnectIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    );
                  })}

                  {bankConnections.length > 0 && (
                    <Box
                      onClick={() => setBanksOverflowOpen(true)}
                      sx={{
                        border: `1px solid ${palette.tokens.hairline}`, borderRadius: 2, bgcolor: 'transparent',
                        cursor: 'pointer', transition: 'background-color 0.15s ease', p: 1.25, textAlign: 'center',
                        '&:hover': { bgcolor: palette.tokens.surfaceSoft },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: 13, color: palette.tokens.primary }}>
                        {overflowCount > 0 ? `See ${overflowCount} more` : 'Manage banks'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })() : (() => {
              if (bankStatements.length === 0) {
                return (
                  <Paper elevation={0} sx={{
                    border: `2px dashed ${palette.tokens.hairline}`, borderRadius: 3, p: 2,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                    flex: 1,
                  }}>
                    <StatementIcon sx={{ fontSize: 26, color: palette.tokens.hairline }} />
                    <Typography sx={{ fontWeight: 700, color: palette.tokens.ink, fontSize: 13.5, textAlign: 'center' }}>No statements uploaded yet</Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddBankIcon sx={{ fontSize: 17 }} />}
                      onClick={() => setBankStatementUploadOpen(true)}
                      sx={{ borderRadius: 2, fontWeight: 600 }}
                    >
                      Upload a statement
                    </Button>
                  </Paper>
                );
              }
              const overflowCount = bankStatements.length - BANK_PANEL_CAP;
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {bankStatements.slice(0, BANK_PANEL_CAP).map(stmt => (
                    <Box key={stmt.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: `1px solid ${palette.tokens.hairline}`, borderRadius: 2, p: 1.25 }}>
                      <StatementIcon sx={{ fontSize: 15, color: palette.tokens.muted, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ fontWeight: 700, color: palette.tokens.ink, fontSize: 13, minWidth: 0, flex: 1 }} noWrap>
                        {statementPeriodLabel(stmt)}
                        <Box component="span" sx={{ color: palette.tokens.mutedSoft, fontWeight: 500 }}> · {stmt.transaction_count} txn{stmt.transaction_count === 1 ? '' : 's'}</Box>
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <Tooltip title="View transactions">
                          <IconButton size="small" onClick={() => openStatementDetail(stmt.id)} sx={{ color: palette.tokens.muted }}>
                            <ViewIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete statement">
                          <IconButton size="small" onClick={() => setDeleteStatementTarget(stmt.id)} sx={{ color: palette.tokens.muted, '&:hover': { color: palette.tokens.error } }}>
                            <DeleteIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}

                  {bankStatements.length > 0 && (
                    <Box
                      onClick={() => setStatementsListDialogOpen(true)}
                      sx={{
                        border: `1px solid ${palette.tokens.hairline}`, borderRadius: 2, bgcolor: 'transparent',
                        cursor: 'pointer', transition: 'background-color 0.15s ease', p: 1.25, textAlign: 'center',
                        '&:hover': { bgcolor: palette.tokens.surfaceSoft },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: 13, color: palette.tokens.primary }}>
                        {overflowCount > 0 ? `See ${overflowCount} more` : 'Manage statements'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })()}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Manage banks — full per-connection detail (owner assignment, resync, disconnect),
          reached via the sidebar panel's "See N more"/"Manage banks" link above ── */}
      <Dialog open={banksOverflowOpen} onClose={() => setBanksOverflowOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Manage banks</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 2 }}>
          {bankConnections.map(conn => (
            <BankConnectionRow
              key={conn.id}
              conn={conn}
              owners={owners}
              syncing={syncing}
              connecting={connecting}
              onSetOwner={handleSetConnectionOwner}
              onResync={(c) => handleSyncBank(c, true)}
              onDisconnect={setDisconnectTarget}
              onReconnect={() => handleConnectBank()}
            />
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setBanksOverflowOpen(false)} sx={{ color: palette.tokens.muted }}>Close</Button>
          <Button
            variant="outlined"
            onClick={() => setBanksOverflowOpen(false)}
            sx={{ borderRadius: 2 }}
          >
            Save changes
          </Button>
          <Button
            variant="contained"
            startIcon={<AddBankIcon />}
            onClick={() => handleConnectBank()}
            disabled={connecting}
            sx={{ borderRadius: 2 }}
          >
            {connecting ? 'Connecting…' : 'Add another bank'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Income vs Expenses trend ('income' means incoming bank transactions; see the
           monthly-trend fetch above) ── */}
      {(() => {
        return (
        <Paper elevation={0} sx={{ bgcolor: palette.tokens.white, border: `1px solid ${palette.tokens.hairline}`, borderRadius: 2, p: { xs: 1.5, sm: 2.5 }, mb: 3 }}>
          {/* Income/Expenses/Net + "View your transactions" — pixel-aligned with the
              Source/Year/Range/View filters in the hero row above, not just "the same shape".
              The hero row is itself `flex: 3` (filters card) + `flex: 1` (the "Your banks"
              sidebar) inside a `gap: 2` flex row, with its filters padded by
              `p: { xs: 2.7, md: 3.6 }` inside that flex:3 card — so this wrapper reproduces the
              exact same flex:3/flex:1/gap:2 split (with an empty flex:1 spacer standing in for
              the sidebar) and the exact same horizontal padding (cancelling this Paper's own
              padding via a matching negative margin first), so the 4-column grid below (matching
              the hero's now-4-column Source/Year/Range/View row) lands on identical
              x-coordinates to the ones above — Income under Source, Expenses under Year, Net
              under Range, and "View your transactions" under View (rather than floating
              off on its own, right-aligned across the whole card). */}
          <Box sx={{ display: { xs: 'block', md: 'flex' }, gap: 2, mx: { xs: -1.5, sm: -2.5 } }}>
            <Box sx={{ flex: 3, px: { xs: 2.7, md: 3.6 } }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: { xs: 2.25, sm: 2.7 } }}>
                {[
                  { key: 'income', value: fmtGBP(summary.bank_income), label: 'Income', color: TREND_COLORS.income },
                  { key: 'expenses', value: fmtGBP(summary.expenses_general), label: 'Expenses', color: TREND_COLORS.expenses },
                  { key: 'net', value: netForHero.display, label: 'Net', color: TREND_COLORS.net, tooltip: netForHero.tooltip },
                ].map(stat => (
                  <Box key={stat.key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.4 }}>
                      <Tooltip title={stat.tooltip || ''} arrow disableHoverListener={!stat.tooltip}>
                        <Typography variant="displaySm" component="div" sx={{ fontSize: { xs: '1.62rem', md: '1.98rem' }, color: stat.color, fontWeight: 700, lineHeight: 1, textAlign: 'center', fontVariantNumeric: 'lining-nums', cursor: stat.tooltip ? 'help' : 'default' }}>
                        {loading ? '—' : stat.value}
                        </Typography>
                      </Tooltip>
                      {stat.key === 'net' && <CalculatorButton />}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.45 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: stat.color, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.74rem', color: palette.tokens.muted, textAlign: 'center' }}>{stat.label}</Typography>
                    </Box>
                  </Box>
                ))}

                {/* VAT (Victor Ltd) — sum of tax extracted off every confirmed invoice/receipt
                    for the period, split the same way as the Credit(IN)/Debit(OUT) transaction
                    columns below: vat_out is VAT on money spent (reclaimable input VAT), vat_in
                    is VAT on a manually-recorded money-in cash entry (output VAT owed). Purely
                    informational — not folded into Income/Expenses/Net above. Stacked (paid over
                    charged), aligned under the "View" filter, in the same column
                    "View your transactions" used to occupy — that button now lives in the
                    flex:1 sidebar-aligned spacer instead (see below), under "Manage your banks". */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', alignItems: 'baseline', columnGap: 0.6, rowGap: 0.5 }}>
                  <Typography sx={{ fontSize: '0.68rem', color: palette.tokens.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, textAlign: 'right' }}>VAT paid</Typography>
                  <Typography sx={{ fontSize: '0.95rem', color: palette.tokens.ink, fontWeight: 700, fontVariantNumeric: 'lining-nums', textAlign: 'left' }}>{loading ? '—' : fmtGBP(Number(summary.vat_out))}</Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: palette.tokens.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, textAlign: 'right' }}>VAT charged</Typography>
                  <Typography sx={{ fontSize: '0.95rem', color: palette.tokens.ink, fontWeight: 700, fontVariantNumeric: 'lining-nums', textAlign: 'left' }}>{loading ? '—' : fmtGBP(Number(summary.vat_in))}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  if (bankSource !== 'statements') { setBankTxnDialogOpen(true); return; }
                  if (bankStatements.length === 1) { openStatementDetail(bankStatements[0].id); return; }
                  setStatementsListDialogOpen(true);
                }}
                disabled={bankSource === 'statements' ? bankStatements.length === 0 : noTransactionsYet}
                sx={{ borderRadius: 2, fontWeight: 600, fontSize: 13 }}
              >
                View your transactions
              </Button>
            </Box>
          </Box>

          <Box sx={{ mb: 2.5 }} />

          {monthlyTrendLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
          ) : (() => {
            // The x-axis is always every month of the year so far (Jan..current, or the fiscal
            // equivalent) regardless of which granularity's bars are shown — see TrendBars.
            const labels = monthlyTrend.map(m => m.label);

            // Y-domain is computed from whichever dataset is actually being drawn right now, so
            // switching granularity always gets sensible headroom rather than a stale domain
            // sized for a different bar shape.
            const activeTrend = chartGranularity === 'monthly' ? monthlyTrend : chartGranularity === 'quarterly' ? quarterlyTrend : chartGranularity === 'weekly' ? weeklyTrend : dailyTrend;
            const incomeVals = activeTrend.map(m => Number(m.income));
            const expensesValsNeg = activeTrend.map(m => -Number(m.expenses));
            const netVals = activeTrend.map(m => Number(m.net));

            // Force the y-axis to always straddle zero with real headroom on both sides — a
            // month with a big Income bar and no Expenses (or vice versa) would otherwise let the
            // auto-computed domain skew almost entirely to one side, and the topmost/bottommost
            // "nice" tick can end up so close to the domain edge that MUI clips its label off
            // rather than render it (that's why a real June-heavy Income month was showing gridlines
            // only at £0/-£20k/-£40k with no positive tick at all despite a tall Income bar).
            const dataMax = Math.max(0, ...incomeVals, ...netVals);
            const dataMin = Math.min(0, ...expensesValsNeg, ...netVals);
            const span = Math.max(dataMax - dataMin, 1000);
            const yMax = dataMax + span * 0.2;
            const yMin = dataMin - span * 0.2;

            return (
              <Box ref={chartAreaRef} sx={{ position: 'relative' }} onMouseLeave={() => setHoveredBar(null)}>
                <ChartsDataProvider
                  height={360}
                  series={[]}
                  xAxis={[{
                    id: 'x', data: labels, scaleType: 'band', categoryGapRatio: 0,
                    // categoryGapRatio: 0 removes the band scale's own inter-month gap, so
                    // `bandwidth` spans the *entire* distance to the next month with nothing
                    // reserved in between (bandwidth === step) — see TrendBars' daily/weekly
                    // subdivision, which relies on that to place every day/week bar correctly
                    // under its own month's tick (which ChartsXAxis centers under `bandwidth`)
                    // while still butting up snugly against the neighboring month's bars, rather
                    // than needing to trade one off against the other.
                    valueFormatter: (value: string) => value.split(' ')[0], // "Jan 2026" -> "Jan"
                  }]}
                  yAxis={[{ id: 'y', valueFormatter: (v: number) => fmtGBPCompact(v), min: yMin, max: yMax, tickNumber: 5 }]}
                  margin={{ left: 56, right: 16, top: 20, bottom: 44 }}
                >
                  <ChartsSurface>
                    <ChartsGrid horizontal />
                    <TrendBars monthlyTrend={monthlyTrend} quarterlyTrend={quarterlyTrend} weeklyTrend={weeklyTrend} dailyTrend={dailyTrend} granularity={chartGranularity} onHover={handleBarHover} />
                    <QuarterAxisRow monthlyTrend={monthlyTrend} quarterlyTrend={quarterlyTrend} />
                    <ChartsReferenceLine y={0} lineStyle={{ stroke: palette.tokens.mutedSoft, strokeWidth: 1 }} />
                    <ChartsXAxis axisId="x" />
                    <ChartsYAxis axisId="y" />
                  </ChartsSurface>
                </ChartsDataProvider>
                {hoveredBar && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: hoveredBar.x,
                      top: hoveredBar.y,
                      transform: 'translate(-50%, calc(-100% - 10px))',
                      bgcolor: palette.tokens.ink,
                      borderRadius: 1.5,
                      p: 1.25,
                      minWidth: 160,
                      pointerEvents: 'none',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                      zIndex: 10,
                    }}
                  >
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#fff', mb: 0.75 }}>
                      {hoveredBar.heading}
                    </Typography>
                    {([
                      [TREND_COLORS.income, 'Income', hoveredBar.income, false],
                      [TREND_COLORS.expenses, 'Expenses', hoveredBar.expenses, false],
                      [TREND_COLORS.net, 'Net', hoveredBar.net, true],
                    ] as const).map(([color, label, val, signed]) => (
                      <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.35 }}>
                        {/* Net's swatch is a near-black neutral (see TREND_COLORS), which would
                            otherwise disappear against this tooltip's own dark background — a
                            thin light ring keeps it visible without changing its actual color. */}
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, flexShrink: 0, border: color === TREND_COLORS.net ? '1px solid rgba(255,255,255,0.6)' : 'none' }} />
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', flex: 1 }}>{label}</Typography>
                        <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700 }}>
                          {signed && val >= 0 ? '+' : ''}{fmtGBP(val)}
                        </Typography>
                      </Box>
                    ))}
                    {(hoveredBar.vatOut > 0 || hoveredBar.vatIn > 0) && (
                      <Box sx={{ mt: 0.6, pt: 0.6, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                        {hoveredBar.vatOut > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', flex: 1 }}>VAT paid</Typography>
                            <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700 }}>{fmtGBP(hoveredBar.vatOut)}</Typography>
                          </Box>
                        )}
                        {hoveredBar.vatIn > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.35 }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', flex: 1 }}>VAT charged</Typography>
                            <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700 }}>{fmtGBP(hoveredBar.vatIn)}</Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            );
          })()}
        </Paper>
        );
      })()}

      {/* ── uploading a statement now lives in the "Your banks" panel (its "+" button when
          bankSource === 'statements') rather than its own card here — connecting/managing banks
          or statements both live in that one sidebar card next to the welcome hero, above. ── */}

      {/* ── uploaded statements popup — reached from the "Your banks" panel's "See N more"/
          "Manage statements" link when bankSource === 'statements', same role as the "Manage
          banks" dialog plays for connections ── */}
      <Dialog open={statementsListDialogOpen} onClose={() => setStatementsListDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Uploaded statements</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {bankStatementsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={20} /></Box>
          ) : bankStatements.length === 0 ? (
            <Typography variant="body2" sx={{ color: palette.tokens.mutedSoft, textAlign: 'center', py: 4 }}>No statements uploaded yet.</Typography>
          ) : (
            bankStatements.map((stmt, idx, arr) => {
              const hasMismatch = stmt.debits_mismatch || stmt.credits_mismatch || stmt.balance_mismatch;
              return (
                <Box key={stmt.id}>
                  <Box
                    onClick={() => openStatementDetail(stmt.id)}
                    sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', gap: 1, '&:hover': { bgcolor: palette.tokens.surfaceSoft } }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.25, minWidth: 0 }}>
                      <StatementThumbnail statementId={stmt.id} createdAt={stmt.created_at} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: palette.tokens.ink, fontSize: 14 }} noWrap>
                          {statementPeriodLabel(stmt)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft }} noWrap>
                          {stmt.bank_name || 'Bank statement'}{stmt.account_number ? ` ····${stmt.account_number.slice(-4)}` : ''}
                          {` · ${stmt.transaction_count} transaction${stmt.transaction_count === 1 ? '' : 's'}`}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      {hasMismatch && (
                        <Tooltip title="Extracted totals don't add up — open the statement to see flagged rows">
                          <Chip icon={<WarningIcon sx={{ fontSize: '14px !important' }} />} label="Check totals" size="small" sx={{ bgcolor: alpha(palette.tokens.warning, 0.15), color: palette.tokens.warning, fontWeight: 700, fontSize: 10, height: 20 }} />
                        </Tooltip>
                      )}
                      <Tooltip title="Delete statement">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteStatementTarget(stmt.id); }} sx={{ color: palette.tokens.muted, '&:hover': { color: palette.tokens.error } }}>
                          <DeleteIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {idx < arr.length - 1 && <Divider sx={{ borderColor: palette.tokens.surfaceSoft }} />}
                </Box>
              );
            })
          )}
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddBankIcon sx={{ fontSize: 15 }} />}
            onClick={() => setBankStatementUploadOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            Upload another statement
          </Button>
          <Button onClick={() => setStatementsListDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Receipts & invoices — flat, no property/flat tree (that's the certificates pattern; an
          address is still optional per-item metadata, editable via each card's Edit dialog, but
          doesn't drive how these are organized) ── */}
      {invoicesLoading ? (
        <Box sx={{ mt: 5, display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ mt: 5 }}>
          {/* Same heading treatment as PropertiesPage.tsx's "Portfolio" section header
              (displaySm serif h3 + hairline rule filling the row), so this section reads as
              part of the same family as the Properties/Certificates trees. */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="displaySm" component="h3" sx={{ fontSize: '2rem', fontWeight: 700, color: palette.tokens.ink, whiteSpace: 'nowrap' }}>
                Invoices &amp; receipts
              </Typography>
              <Box sx={{ flex: 1, height: '1px', bgcolor: palette.tokens.hairline }} />
            </Box>
          </Box>

          {/* ── Pending confirmation — a standalone upload that hasn't yet been told whether
              it's a new expense or proof of an existing transaction; excluded from every total
              until resolved (see Invoice.confirmed). Rendered as real ReceiptCard/InvoiceCard
              instances (not a stripped-down row) so the same Edit/Download/Delete/View-items
              actions are available here too — each card's own "Link or confirm" button (next to
              its "Pending" chip) opens the same review dialog as the post-upload prompt. ── */}
          {pendingInvoiceRecords.length > 0 && (
            <Paper elevation={0} sx={{
              border: `1px solid ${alpha(palette.tokens.warning, 0.3)}`,
              background: alpha(palette.tokens.warning, 0.06), borderRadius: 3, p: 2.5, mb: 4,
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.tokens.ink, mb: 0.5 }}>
                Pending confirmation ({pendingInvoiceRecords.length})
              </Typography>
              <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, display: 'block', mb: 1.5 }}>
                Not counted in your totals yet — link each one to a transaction, or confirm it as a new expense.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 2 }}>
                {pendingInvoiceRecords.map(inv => (
                  inv.document_type === 'Receipt' ? (
                    <ReceiptCard
                      key={inv.id} invoice={inv} onUpdate={refreshInvoicesAndSummary} onDelete={(id) => setDeleteInvoiceTarget(id)}
                      onReview={(r) => setPendingConfirmQueue([{ id: r.id, contractor_name: r.contractor_name, invoice_date: r.invoice_date, total: r.total, document_type: 'Receipt' }])}
                    />
                  ) : (
                    <InvoiceCard
                      key={inv.id} invoice={inv} onUpdate={refreshInvoicesAndSummary} onDelete={(id) => setDeleteInvoiceTarget(id)}
                      onReview={(r) => setPendingConfirmQueue([{ id: r.id, contractor_name: r.contractor_name, invoice_date: r.invoice_date, total: r.total, document_type: 'Invoice' }])}
                    />
                  )
                ))}
              </Box>
            </Paper>
          )}

          {/* ── Receipts / Invoices tabs — same pseudo-tab pattern as PropertiesPage.tsx's flat
              tabs: click a tab, only its cards show below. Each tab carries its own label, total,
              and Upload button — no need to hunt for the upload action separately once you've
              picked a tab. ── */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {([
              { key: 'invoices' as const, label: 'Invoices', total: invoicesTotal, uploadLabel: 'Upload invoice', onUpload: () => setInvoiceUploadOpen(true), eligibleForBulkAdd: invoicesEligibleForBulkAdd, shownAsTransactions: invoicesShownAsTransactions },
              { key: 'receipts' as const, label: 'Receipts', total: receiptsTotal, uploadLabel: 'Upload receipt', onUpload: () => setReceiptUploadOpen(true), eligibleForBulkAdd: receiptsEligibleForBulkAdd, shownAsTransactions: receiptsShownAsTransactions },
            ]).map(tab => {
              const isSelected = invoiceTab === tab.key;
              return (
                <Box
                  key={tab.key}
                  onClick={() => setInvoiceTab(tab.key)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.5,
                    borderRadius: '10px 10px 0 0',
                    bgcolor: isSelected ? palette.tokens.white : 'transparent',
                    border: isSelected ? `1px solid ${palette.tokens.hairline}` : '1px solid transparent',
                    borderBottom: isSelected ? `1px solid ${palette.tokens.white}` : 'none',
                    mb: isSelected ? '-1px' : 0,
                    cursor: 'pointer',
                    position: 'relative',
                    zIndex: isSelected ? 1 : 0,
                    transition: 'background-color 0.15s ease',
                    '&:hover': !isSelected ? { bgcolor: alpha(palette.tokens.white, 0.5) } : {},
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography sx={{ fontWeight: isSelected ? 700 : 500, color: isSelected ? palette.tokens.ink : palette.tokens.muted, whiteSpace: 'nowrap' }}>
                      {tab.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, whiteSpace: 'nowrap' }}>
                      · {fmtStatementAmount(tab.total, 'GBP')}
                    </Typography>
                  </Box>
                  {tab.eligibleForBulkAdd.length > 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => { e.stopPropagation(); setBulkAddDocType(tab.key); }}
                      sx={{ borderRadius: 2, fontWeight: 600, fontSize: 12, flexShrink: 0, textTransform: 'none', borderColor: palette.tokens.hairline, color: palette.tokens.muted }}
                    >
                      Add all ({tab.eligibleForBulkAdd.length})
                    </Button>
                  )}
                  {tab.shownAsTransactions.length > 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => { e.stopPropagation(); handleUnlinkAllFromTransactions(tab.shownAsTransactions); }}
                      sx={{ borderRadius: 2, fontWeight: 600, fontSize: 12, flexShrink: 0, textTransform: 'none', borderColor: palette.tokens.hairline, color: palette.tokens.muted }}
                    >
                      Unlink all ({tab.shownAsTransactions.length})
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    onClick={(e) => { e.stopPropagation(); tab.onUpload(); }}
                    sx={{ borderRadius: 2, fontWeight: 600, fontSize: 12, bgcolor: palette.tokens.primary, color: palette.tokens.white, flexShrink: 0, '&:hover': { bgcolor: palette.tokens.primaryActive } }}
                  >
                    {tab.uploadLabel}
                  </Button>
                </Box>
              );
            })}
          </Box>

          {/* ── Content panel for the selected tab — same "cut corner" pairing with the tab row
              above as PropertiesPage.tsx's flat detail panel. ── */}
          <Box sx={{ bgcolor: palette.tokens.white, border: `1px solid ${palette.tokens.hairline}`, borderRadius: '0 8px 8px 8px', p: 2.5, mb: 4 }}>
            {invoiceTab === 'receipts' ? (
              receiptsConfirmed.length === 0 ? (
                <Paper elevation={0} sx={{
                  border: `2px dashed ${palette.tokens.hairline}`, borderRadius: 3, p: 6,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                }}>
                  <PhotoIcon sx={{ fontSize: 48, color: palette.tokens.hairline }} />
                  <Typography variant="h6" sx={{ color: palette.tokens.muted, fontWeight: 600 }}>No receipts yet</Typography>
                  <Typography variant="body2" sx={{ color: palette.tokens.mutedSoft, textAlign: 'center', maxWidth: 360 }}>
                    Upload a photo of a receipt and we'll extract the details automatically. Attaching it to a property is optional.
                  </Typography>
                  <Button variant="outlined" startIcon={<PhotoIcon />} onClick={() => setReceiptUploadOpen(true)} sx={{ borderRadius: 2, borderColor: palette.tokens.hairline, color: palette.tokens.muted }}>
                    Upload your first receipt
                  </Button>
                </Paper>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 2 }}>
                  {receiptsConfirmed.map(inv => (
                    <ReceiptCard
                      key={inv.id} invoice={inv} onUpdate={refreshInvoicesAndSummary} onDelete={(id) => setDeleteInvoiceTarget(id)}
                      onReview={(r) => setPendingConfirmQueue([{ id: r.id, contractor_name: r.contractor_name, invoice_date: r.invoice_date, total: r.total, document_type: 'Receipt' }])}
                    />
                  ))}
                </Box>
              )
            ) : (
              invoicesConfirmed.length === 0 ? (
                <Paper elevation={0} sx={{
                  border: `2px dashed ${palette.tokens.hairline}`, borderRadius: 3, p: 6,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                }}>
                  <ReceiptIcon sx={{ fontSize: 48, color: palette.tokens.hairline }} />
                  <Typography variant="h6" sx={{ color: palette.tokens.muted, fontWeight: 600 }}>No invoices yet</Typography>
                  <Typography variant="body2" sx={{ color: palette.tokens.mutedSoft, textAlign: 'center', maxWidth: 360 }}>
                    Upload a contractor invoice and we'll extract the details automatically. Attaching it to a property is optional.
                  </Typography>
                  <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setInvoiceUploadOpen(true)} sx={{ borderRadius: 2 }}>
                    Upload your first invoice
                  </Button>
                </Paper>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 2 }}>
                  {invoicesConfirmed.map(inv => (
                    <InvoiceCard
                      key={inv.id} invoice={inv} onUpdate={refreshInvoicesAndSummary} onDelete={(id) => setDeleteInvoiceTarget(id)}
                      onReview={(r) => setPendingConfirmQueue([{ id: r.id, contractor_name: r.contractor_name, invoice_date: r.invoice_date, total: r.total, document_type: 'Invoice' }])}
                    />
                  ))}
                </Box>
              )
            )}
          </Box>
        </Box>
      )}

      <InvoiceUpload
        open={invoiceUploadOpen}
        onClose={() => setInvoiceUploadOpen(false)}
        onUploadSuccess={refreshInvoicesAndSummary}
        onNewPendingInvoices={(invs) => setPendingConfirmQueue(prev => [...prev, ...invs])}
      />

      <ReceiptUpload
        open={receiptUploadOpen}
        onClose={() => setReceiptUploadOpen(false)}
        onUploadSuccess={refreshInvoicesAndSummary}
        onNewPendingInvoices={(invs) => setPendingConfirmQueue(prev => [...prev, ...invs])}
      />

      <ConfirmNewExpenseDialog
        open={pendingConfirmQueue.length === 1}
        invoice={pendingConfirmQueue[0] || null}
        onDismiss={() => setPendingConfirmQueue(prev => prev.slice(1))}
        onResolved={() => {
          setPendingConfirmQueue(prev => prev.slice(1));
          refreshInvoicesAndSummary();
        }}
      />

      {/* Several receipts/invoices uploaded in the same batch — a one-page summary (title + total
          + per-item actions) instead of the one-at-a-time flow above, plus "Add all as new"/"I'll
          do it later" to clear the whole batch without leaving anything stuck pending. */}
      <BulkInvoiceReviewDialog
        open={pendingConfirmQueue.length > 1}
        invoices={pendingConfirmQueue}
        onResolved={(ids) => {
          setPendingConfirmQueue(prev => prev.filter(inv => !ids.includes(inv.id)));
          refreshInvoicesAndSummary();
        }}
        onClose={() => setPendingConfirmQueue([])}
      />

      {/* "Add all" shortcut next to each tab's Upload button — every invoice or receipt (scoped
          to whichever tab it was clicked from) not currently showing as a transaction (still
          pending, or previously deferred/hidden), reviewed the same way as right after a bulk
          upload. */}
      <BulkInvoiceReviewDialog
        open={bulkAddDocType !== null}
        invoices={bulkAddDocType === 'receipts' ? receiptsEligibleForBulkAdd : invoicesEligibleForBulkAdd}
        onResolved={() => refreshInvoicesAndSummary()}
        onClose={() => setBulkAddDocType(null)}
      />

      <BankStatementUpload
        open={bankStatementUploadOpen}
        onClose={() => setBankStatementUploadOpen(false)}
        onUploadSuccess={() => {
          fetchBankStatements(true);
          // A newly uploaded statement's transactions feed straight into Income/Expenses for
          // whatever month(s) they fall in — refresh the KPI cards and trend chart immediately,
          // same silent-refresh pattern used for invoice/receipt uploads.
          fetchData(true);
          fetchMonthlyTrend(true);
        }}
        owners={owners}
        defaultOwnerId={selectedOwnerId}
      />

      <BankConnectionDetailDialog
        open={bankTxnDialogOpen}
        onClose={() => setBankTxnDialogOpen(false)}
        connections={mergedBankConnections}
        transactions={mergedBankTransactions}
        onUploadProof={(bankTransactionId) => setUploadProofTarget({ bank_transaction_id: bankTransactionId })}
        onViewProof={(proof) => setViewProofTarget(proof)}
        onToggleExcluded={(transactionId, excluded) => handleToggleBankTxnExcluded(transactionId, excluded)}
        onBulkToggleExcluded={(transactionIds, excluded) => handleBulkToggleBankTxnExcluded(transactionIds, excluded)}
        onSetTag={(transactionId, tag) => handleSetBankTxnTag(transactionId, tag)}
        onBulkSetTag={(transactionIds, tag) => handleBulkSetBankTxnTag(transactionIds, tag)}
        onRemoveCash={handleRemoveCashTransaction}
        initialSelectedConnectionIds={bankTxnDialogInitialConnectionIds}
        initialExpandedMonthKeys={bankTxnDialogInitialMonthKeys}
        onAddNewTransaction={() => setAddTransactionDialogOpen(true)}
        onEditCashTransaction={handleEditCashTransaction}
      />

      <AddCashTransactionDialog
        open={addTransactionDialogOpen}
        onClose={() => setAddTransactionDialogOpen(false)}
        eligibleDocuments={eligibleDocsForNewTransaction}
        onPickDocument={handlePickDocumentForNewTransaction}
        onCreateManual={handleCreateManualTransaction}
        onUploadNew={handleUploadNewCashDocument}
        onUseDuplicateDocument={handleUseDuplicateCashDocument}
        onManualEntryCreated={handleManualEntryCreatedForCash}
      />

      <BankStatementDetailDialog
        open={!!expandedStatementId}
        onClose={() => setExpandedStatementId(null)}
        statement={bankStatements.find(s => s.id === expandedStatementId) || null}
        transactions={expandedStatementId ? statementDetail[expandedStatementId] : undefined}
        loading={loadingStatementDetail === expandedStatementId}
        onTransactionUpdated={(updated) => {
          if (!expandedStatementId) return;
          setStatementDetail(prev => ({
            ...prev,
            [expandedStatementId]: (prev[expandedStatementId] || []).map(t => (t.id === updated.id ? updated : t)),
          }));
          // Correcting a debit/credit amount changes that month's Income/Expenses — refresh both.
          fetchData(true);
          fetchMonthlyTrend(true);
        }}
        onTransactionCreated={(created) => {
          if (!expandedStatementId) return;
          setStatementDetail(prev => ({
            ...prev,
            [expandedStatementId]: [...(prev[expandedStatementId] || []), created],
          }));
          // A new manual row counts towards that month's Income/Expenses same as any other.
          fetchData(true);
          fetchMonthlyTrend(true);
        }}
        onUploadProof={(bankStatementTransactionId) => setUploadProofTarget({ bank_statement_transaction_id: bankStatementTransactionId })}
        onViewProof={(proof) => setViewProofTarget(proof)}
      />

      <UploadProofDialog
        open={!!uploadProofTarget}
        onClose={() => setUploadProofTarget(null)}
        linkTarget={uploadProofTarget || {}}
        onSuccess={refreshAfterProofChange}
      />

      <ProofViewDialog
        open={!!viewProofTarget}
        onClose={() => setViewProofTarget(null)}
        proof={viewProofTarget}
        onChanged={refreshAfterProofChange}
        onDeleteRequested={(id) => setDeleteInvoiceTarget(id)}
        isCash={!!viewProofTarget && !viewProofTarget.bank_transaction_id && !viewProofTarget.bank_statement_transaction_id}
      />

      {/* ── Delete bank statement confirmation ── */}
      <Dialog open={!!deleteStatementTarget} onClose={() => setDeleteStatementTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete bank statement?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.tokens.muted }}>
            This will permanently delete this statement's digital record and its PDF file. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteStatementTarget(null)} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteStatement} sx={{ borderRadius: 2 }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete invoice confirmation ── */}
      <Dialog open={!!deleteInvoiceTarget} onClose={() => { setDeleteInvoiceTarget(null); setDeleteInvoiceError(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete invoice?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.tokens.muted }}>
            This will permanently delete this invoice and its PDF file. This cannot be undone.
          </Typography>
          {deleteInvoiceError && (
            <Alert severity="error" sx={{ mt: 2 }}>{deleteInvoiceError}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => { setDeleteInvoiceTarget(null); setDeleteInvoiceError(null); }} disabled={deletingInvoice} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteInvoice} disabled={deletingInvoice} sx={{ borderRadius: 2 }}>
            {deletingInvoice ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Disconnect bank confirmation ── */}
      <Dialog open={!!disconnectTarget} onClose={() => setDisconnectTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Disconnect bank?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.tokens.muted }}>
            This will disconnect <strong>{disconnectTarget?.institution_name}</strong> and delete its stored transactions.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDisconnectTarget(null)} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDisconnectBank} sx={{ borderRadius: 2 }}>Disconnect</Button>
        </DialogActions>
      </Dialog>

      {/* ── Disconnect all banks confirmation ── */}
      <Dialog open={disconnectAllConfirmOpen} onClose={() => setDisconnectAllConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Disconnect all banks?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.tokens.muted }}>
            This will disconnect all {bankConnections.length} connected {bankConnections.length === 1 ? 'bank' : 'banks'} and delete their stored transactions.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDisconnectAllConfirmOpen(false)} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDisconnectAllBanks} sx={{ borderRadius: 2 }}>Disconnect all</Button>
        </DialogActions>
      </Dialog>

      {/* ── Bank data consent ── */}
      <Dialog open={consentDialogOpen} onClose={() => setConsentDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Connect your bank</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.tokens.muted }}>
            By connecting your bank, you consent to MillOS collecting and storing your transaction data via
            TrueLayer. Transaction data is encrypted at rest and retained for
            up to 24 months. See our{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> for details.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConsentDialogOpen(false)} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAcceptBankConsent}
            disabled={acceptingConsent}
            sx={{ borderRadius: 2 }}
          >
            {acceptingConsent ? 'Saving…' : 'I agree'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── MFA code ── */}
      <Dialog open={mfaDialogOpen} onClose={() => setMfaDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Verify it's you</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.tokens.muted, mb: 2 }}>
            Enter the 6-digit code from your authenticator app to connect your bank.
          </Typography>
          {mfaError && (
            <Typography variant="body2" sx={{ color: palette.tokens.error, mb: 1 }}>{mfaError}</Typography>
          )}
          <TextField
            size="small"
            label="6-digit code"
            value={mfaCode}
            onChange={e => setMfaCode(e.target.value)}
            fullWidth
            autoFocus
            disabled={connecting}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setMfaDialogOpen(false)} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handleConnectBank(mfaCode)}
            disabled={connecting || !mfaCode.trim()}
            sx={{ borderRadius: 2 }}
          >
            {connecting ? 'Verifying…' : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inline "Set up 2FA" — opened instead of navigating to /profile when handleConnectBank's
          mfa_enrollment_required response comes back. Resets to the plain "Set up" prompt every
          time it (re)opens, same as ProfilePage.tsx's card starting point. */}
      <Dialog
        open={mfaEnrollDialogOpen}
        onClose={() => { setMfaEnrollDialogOpen(false); setMfaQrCode(null); setMfaSecret(null); setMfaConfirmCode(''); setMfaEnrollError(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <ShieldIcon sx={{ color: palette.tokens.primary }} />
          Two-Factor Authentication
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Required before connecting a bank account. Uses an authenticator app (e.g. Google Authenticator, Authy).
          </Typography>

          {mfaEnrollError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMfaEnrollError(null)}>
              {mfaEnrollError}
            </Alert>
          )}

          {mfaQrCode ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Step 1 of 2 — scan this with your authenticator app
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 2 }}>
                <img src={mfaQrCode} alt="MFA QR code" style={{ width: 160, height: 160, border: `1px solid ${palette.tokens.hairline}`, borderRadius: 8 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Can't scan it? Type this code instead:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                    {mfaSecret}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Step 2 of 2 — open the account you just added in your authenticator app and enter the 6-digit code it's showing
              </Typography>
              <TextField
                size="small"
                label="6-digit code"
                value={mfaConfirmCode}
                onChange={e => setMfaConfirmCode(e.target.value)}
                disabled={mfaConfirming}
                autoFocus
                fullWidth
              />
            </Box>
          ) : (
            <Button
              variant="outlined"
              onClick={handleStartMfaEnroll}
              disabled={mfaEnrolling}
              startIcon={mfaEnrolling ? <CircularProgress size={16} /> : <ShieldIcon />}
              sx={{ borderRadius: 2 }}
            >
              {mfaEnrolling ? 'Starting…' : 'Set up'}
            </Button>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => { setMfaEnrollDialogOpen(false); setMfaQrCode(null); setMfaSecret(null); setMfaConfirmCode(''); setMfaEnrollError(null); }}
            sx={{ color: palette.tokens.muted }}
          >
            Cancel
          </Button>
          {mfaQrCode && (
            <Button
              variant="contained"
              onClick={handleConfirmMfaEnroll}
              disabled={mfaConfirming || !mfaConfirmCode.trim()}
              startIcon={mfaConfirming ? <CircularProgress size={16} /> : undefined}
              sx={{ borderRadius: 2 }}
            >
              {mfaConfirming ? 'Confirming…' : 'Confirm'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountingPage;
